// src/services/assist-input.ts
// Adapted from assist_css_js_sample/selectEnqueteInputAid.js

/**
 * 質問項目の型定義
 */
interface QuestionItem {
  questionHtml?: string;
  next?: string | number;
  branchMap?: Record<string, string | number | null>;
  questionType?: string;
  required?: string | boolean;
  contentreflectflag?: string | boolean;
  answerafterline?: string | number;
  inputaidsubstitutiontype?: string | number;
  defaultanswertext?: string;
  enabled?: boolean;
  [key: string]: any;
}

/**
 * HTMLInputElementかHTMLSelectElementのみを引数として受け付ける型
 */
type FormElement = HTMLInputElement | HTMLSelectElement;

/**
 * State class to manage the state of the form
 */
export class AssistInputState {
  no: number = 0;
  num: number = 0;
  aptoenq: HTMLElement | null = null;
  question: QuestionItem[] = [];
  css: Array<[string, string]> = []; // セレクタとCSSルールの配列
  backup: Record<number, HTMLElement> = {};

  constructor(questionList: QuestionItem[], css: Array<any>) {
    // 配列であることを確認
    if (!Array.isArray(questionList)) {
      // 空の配列で初期化
      this.question = [];
    } else {
      this.question = questionList;
    }
    
    // CSS設定の確認
    if (!Array.isArray(css)) {
      this.css = [];
    } else {
      this.css = css;
    }
    
    // aptoenq要素の取得を確実にするため、要素がなければ作成する
    this.aptoenq = document.getElementById('aptoenqform');
    if (!this.aptoenq) {
      // モーダル内で新しいaptoenqform要素を作成
      const modalContent = document.querySelector('.selectEnqueteInputAid .ant-modal-body');
      if (modalContent) {
        const existingElement = modalContent.querySelector('#aptoenqform');
        if (existingElement) {
          this.aptoenq = existingElement as HTMLElement;
        } else {
          // コンテナを探す
          const container = modalContent.querySelector('[style*="border"]');
          if (container) {
            // 既存の要素の状態を確認
            const existingChild = container.querySelector('#aptoenqform');
            if (existingChild) {
              this.aptoenq = existingChild as HTMLElement;
            } else {
              // 新しい要素を作成
              const newElement = document.createElement('div');
              newElement.id = 'aptoenqform';
              newElement.className = 'fontM';
              
              // 最初の要素として挿入
              if (container.firstChild) {
                container.insertBefore(newElement, container.firstChild);
              } else {
                container.appendChild(newElement);
              }
              
              this.aptoenq = newElement;
            }
          }
        }
      }
    }
    
    // Initialize form
    this.deleteAfter(0);
    this.num = 0;

    // Find the highest question number
    if (Array.isArray(this.question)) {
      for (let i in this.question) {
        if (typeof this.question[i] === 'function') {
          continue;
        }
        const n = parseInt(i);
        if (!isNaN(n) && n > this.num) {
          this.num = n;
        }
      }
    }

    // Add reset button handler if element exists
    const resetButton = document.getElementById('selectEnqueteInputAid_btnReset');
    if (resetButton) {
      resetButton.removeEventListener('click', () => {});
      resetButton.addEventListener('click', () => {
        this.reset();
      });
    }
    
    // デフォルトの状態を設定 - 0ではなく正の値にする
    this.no = 0;
    
    // 全ての質問を一度生成（新しい方式）
    if (this.aptoenq && this.question.length > 0) {
      
      // まずフォーム内容をクリア
      this.aptoenq.innerHTML = '';
      
      // 全ての質問を作成して追加
      // 初期表示すべき質問のインデックスを特定
      // 最初の設問と分岐なし設問タイプの設問を初期表示
      let initialQuestionIndices = this.getInitialDisplayQuestions();
      
      // 全ての質問を作成して追加
      for (let i = 0; i < this.question.length; i++) {
        // 質問要素を作成
        const questionElement = this.createQuestionElement(i);
        if (questionElement) {
          // 初期表示対象以外は非表示に
          if (!initialQuestionIndices.includes(i)) {
            questionElement.style.display = 'none';
          }
          
          // フォームに追加
          this.aptoenq.appendChild(questionElement);
          
          // イベントハンドラを設定
          this.setupEventHandlers(questionElement, i);
          
          // 有効としてマーク
          this.question[i].enabled = true;
        }
      }
      
      // CSSを適用
      this.applyCss();

      // 回答欄なしタイプのスタイルを適用（DOM追加後）
      for (let i = 0; i < this.question.length; i++) {
        if (this.isHeadingOnlyQuestion(i)) {
          console.log(`質問${i}: DOM要素を検索中...`);
          const questionElement = this.aptoenq.querySelector(`.aptoenq_qa[quesNo="${i}"]`) as HTMLElement;
          console.log(`質問${i}: DOM要素が見つかった = ${!!questionElement}`);

          if (questionElement) {
            console.log(`質問${i}のouterHTML:`, questionElement.outerHTML.substring(0, 500));
            this.applyHeadingOnlyStyles(questionElement, i);
          } else {
            // 別の方法で検索してみる
            const allQuestions = this.aptoenq.querySelectorAll('.aptoenq_qa');
            console.log(`全質問要素の数: ${allQuestions.length}`);
            if (allQuestions[i]) {
              console.log(`インデックス${i}で要素を発見、スタイルを適用`);
              this.applyHeadingOnlyStyles(allQuestions[i] as HTMLElement, i);
            }
          }
        }
      }

      // 初期表示後の追加処理
      // 特に明示的に選択肢が選択されているフォーム要素に対しては、その状態に基づいて表示制御を行う
      
      // 1. まずすべての質問要素を取得し、所定の状態に設定
      const allQuestions = this.aptoenq.querySelectorAll('.aptoenq_qa');
      
      // 2. プルダウンで初期値が選択されている場合の分岐処理
      // これは特に「次の質問」が表示されるように正しく分岐させるため
      // 初期表示のインデックスリストでは、初期状態で選択されているプルダウンの値が考慮されている必要がある
      const selectElements = this.aptoenq.querySelectorAll('select');
      const processedQuestions = new Set<number>(); // 処理済み質問番号
      
      // 選択肢が選択された状態のセレクトボックスを取得して処理する
      selectElements.forEach((select) => {
        const selectElement = select as HTMLSelectElement;
        
        // この質問のインデックスを取得
        const questionElement = selectElement.closest('.aptoenq_qa') as HTMLElement;
        if (!questionElement) return;
        
        const questionIndex = parseInt(questionElement.getAttribute('quesNo') || '0', 10);
        
        // 一度処理した質問は再処理しない
        if (processedQuestions.has(questionIndex)) return;
        processedQuestions.add(questionIndex);
        
        // 選択肢が選択されているか確認
        let hasSelection = false;
        let selectedValue = '';
        
        // select属性のあるoptionを探す
        for (let i = 0; i < selectElement.options.length; i++) {
          const option = selectElement.options[i];
          if (option.hasAttribute('selected')) {
            selectedValue = option.value;
            selectElement.selectedIndex = i;
            hasSelection = true;
            break;
          }
        }
        
        // または現在selectedIndexが0以外ならそれを使用
        if (!hasSelection && selectElement.selectedIndex > 0) {
          selectedValue = selectElement.options[selectElement.selectedIndex].value;
          hasSelection = true;
        }
        
        // 選択肢が選択されている場合、分岐処理を実行
        if (hasSelection && selectedValue) {
          // この質問のbranchMapを取得
          const branchMap = this.question[questionIndex].branchMap;
          if (branchMap && Object.prototype.hasOwnProperty.call(branchMap, selectedValue)) {
            const branchValue = branchMap[selectedValue];
            
            // 分岐値が有効な数値なら、対応する質問を表示
            if (branchValue !== null && branchValue !== undefined && branchValue !== "") {
              const targetIndex = parseInt(String(branchValue), 10) - 1;
              
              if (targetIndex >= 0 && targetIndex < allQuestions.length) {
                (allQuestions[targetIndex] as HTMLElement).style.display = '';
                
                // 分岐先の質問から連続する非分岐質問も表示
                this.showSequentialQuestions(targetIndex);
              }
            }
          }
        }
      });
      
      // 3. チェックボックスやラジオボタンについても同様に処理
      const checkedInputs = this.aptoenq.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      
      checkedInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        
        // この質問のインデックスを取得
        const questionElement = inputElement.closest('.aptoenq_qa') as HTMLElement;
        if (!questionElement) return;
        
        const questionIndex = parseInt(questionElement.getAttribute('quesNo') || '0', 10);
        
        // 一度処理した質問は再処理しない
        if (processedQuestions.has(questionIndex)) return;
        processedQuestions.add(questionIndex);
        
        const inputValue = inputElement.value;
        
        // この質問のbranchMapを取得
        const branchMap = this.question[questionIndex].branchMap;
        if (branchMap && Object.prototype.hasOwnProperty.call(branchMap, inputValue)) {
          const branchValue = branchMap[inputValue];
          
          // 分岐値が有効な数値なら、対応する質問を表示
          if (branchValue !== null && branchValue !== undefined && branchValue !== "") {
            const targetIndex = parseInt(String(branchValue), 10) - 1;
            
            if (targetIndex >= 0 && targetIndex < allQuestions.length) {
              (allQuestions[targetIndex] as HTMLElement).style.display = '';
              
              // 分岐先の質問から連続する非分岐質問も表示
              this.showSequentialQuestions(targetIndex);
            }
          }
        }
      });
    }
    
    // Constructor complete
  }
  
  /**
   * 質問要素を作成する
   */
  private createQuestionElement(questionIndex: number): HTMLElement | null {
    if (!this.question[questionIndex] || !this.question[questionIndex].questionHtml) {
      return null;
    }
    
    // 質問HTMLを取得
    const currentQ = this.question[questionIndex].questionHtml?.replace(/&nbsp;/g, '') || '';
    
    // HTML要素を作成
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentQ;
    
    // この時点でselected属性のあるoptionを処理
    const selectElements = tempDiv.querySelectorAll('select');
    selectElements.forEach(select => {
      const selectedOption = select.querySelector('option[selected]');
      if (selectedOption) {
        const selectedValue = selectedOption.getAttribute('value');
        // select要素にvalue属性としても設定
        if (selectedValue) {
          select.value = selectedValue;
        }
        
        // selectedIndexも設定
        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].hasAttribute('selected')) {
            select.selectedIndex = i;
            break;
          }
        }
      }
    });
    
    // テキストエリアやテキスト入力の初期値を設定
    const questionType = this.question[questionIndex].questionType?.toString();
    if (questionType === '4') {
      const defaultText = this.question[questionIndex].defaultanswertext;
      if (defaultText) {
        const textarea = tempDiv.querySelector('textarea');
        if (textarea) {
          
          // value属性とHTMLの両方を設定
          textarea.value = defaultText;
          textarea.innerHTML = defaultText;
          
          // DOM追加後の処理を保証するためDOMContentLoadedイベント後に再度設定
          setTimeout(() => {
            if (textarea && document.body.contains(textarea)) {
              textarea.value = defaultText;
            }
          }, 100);
        }
      }
    }
    
    const element = tempDiv.firstElementChild as HTMLElement || tempDiv;
    
    if (!element) {
      return null;
    }
    
    // 必要な属性を設定
    if (element.tagName !== 'DIV') {
      // Wrapping non-DIV element
      const wrapper = document.createElement('div');
      wrapper.className = 'aptoenq_qa';
      wrapper.appendChild(element.cloneNode(true));
      
      // 属性を設定
      wrapper.setAttribute('quesNo', questionIndex.toString());
      wrapper.setAttribute('data-question-index', questionIndex.toString());
      wrapper.setAttribute('data-type', this.question[questionIndex].questionType?.toString() || '');
      // 1. APIのrequired値を確認
      const requiredValue = this.question[questionIndex]?.required;
      let isRequired = requiredValue === true || requiredValue === 'true' || requiredValue === '1';
      
      // 2. HTML内に赤い*マークがあるかも確認（最も信頼できるマーカー）
      if (this.question[questionIndex]?.questionHtml) {
        const hasRedAsterisk = this.question[questionIndex]?.questionHtml?.includes('<span class="aptoenq_red">*</span>') || false;
        if (hasRedAsterisk) {
          isRequired = true;
        }
      }
      
      // 最終的な必須状態を設定
      wrapper.setAttribute('data-required', String(isRequired));
      wrapper.setAttribute('data-contentreflectflag', String(this.question[questionIndex].contentreflectflag || false));
      wrapper.setAttribute('data-answerafterline', String(this.question[questionIndex].answerafterline || '0'));
      wrapper.setAttribute('data-inputaidsubstitutiontype', String(this.question[questionIndex].inputaidsubstitutiontype || '0'));

      // 回答欄なしタイプ（見出しのみ）を検出してクラスを追加
      if (this.isHeadingOnlyQuestion(questionIndex)) {
        wrapper.classList.add('heading-only');
        console.log(`createQuestionElement: 質問${questionIndex}のwrapperに heading-only クラスを追加しました`);
      }

      this.restructureLabelsWithAddText(wrapper);
      return wrapper;
    } else {
      // クラス名を確保
      if (!element.className.includes('aptoenq_qa')) {
        element.className = element.className ? element.className + ' aptoenq_qa' : 'aptoenq_qa';
      }

      // 属性を設定
      element.setAttribute('quesNo', questionIndex.toString());
      element.setAttribute('data-question-index', questionIndex.toString());
      element.setAttribute('data-type', this.question[questionIndex].questionType?.toString() || '');
      // 1. APIのrequired値を確認
      const requiredValue = this.question[questionIndex]?.required;
      let isRequired = requiredValue === true || requiredValue === 'true' || requiredValue === '1';

      // 2. HTML内に赤い*マークがあるかも確認（最も信頼できるマーカー）
      if (this.question[questionIndex]?.questionHtml) {
        const hasRedAsterisk = this.question[questionIndex]?.questionHtml?.includes('<span class="aptoenq_red">*</span>') || false;
        if (hasRedAsterisk) {
          isRequired = true;
        }
      }

      // 最終的な必須状態を設定
      element.setAttribute('data-required', String(isRequired));
      element.setAttribute('data-contentreflectflag', String(this.question[questionIndex].contentreflectflag || false));
      element.setAttribute('data-answerafterline', String(this.question[questionIndex].answerafterline || '0'));
      element.setAttribute('data-inputaidsubstitutiontype', String(this.question[questionIndex].inputaidsubstitutiontype || '0'));

      // 回答欄なしタイプ（見出しのみ）を検出してクラスを追加
      if (this.isHeadingOnlyQuestion(questionIndex)) {
        element.classList.add('heading-only');
        console.log(`createQuestionElement: 質問${questionIndex}のelementに heading-only クラスを追加しました`);
      }

      this.restructureLabelsWithAddText(element);
      return element;
    }
  }

  /**
   * add_text（aptoenq_addtext）を含む label.enqtooltip の内部を2グループに分割する。
   * 左グループ（チェックボックス/ラジオボタン）は固定し、
   * 右グループ（ラベルテキスト＋追加テキスト入力）のみが折り返すようにする。
   */
  private restructureLabelsWithAddText(element: HTMLElement): void {
    element.querySelectorAll('label.enqtooltip').forEach(labelEl => {
      const label = labelEl as HTMLElement;
      if (!label.querySelector('input.aptoenq_addtext')) return;

      const leftNode: Element | null =
        label.querySelector('.aptoenq_check') ||
        label.querySelector('input[type="checkbox"], input[type="radio"]');
      if (!leftNode) return;

      const rightGroup = document.createElement('span');
      rightGroup.className = 'enqtooltip-right-group';

      let pastLeft = false;
      for (const child of Array.from(label.childNodes)) {
        if (child === leftNode) {
          pastLeft = true;
          continue;
        }
        if (pastLeft) rightGroup.appendChild(child);
      }

      label.appendChild(rightGroup);
    });
  }

  /**
   * 回答欄なしタイプの質問に対してスタイルを適用する
   */
  private applyHeadingOnlyStyles(element: HTMLElement, questionIndex: number): void {
    console.log(`質問${questionIndex}に回答欄なしスタイルを適用開始`);
    console.log(`要素のクラス: ${element.className}`);
    console.log(`要素のHTML（最初の200文字）: ${element.innerHTML.substring(0, 200)}`);

    // 回答欄を非表示にする（複数のセレクタで試す）
    const answerSelectors = ['.aptoenq_a', 'div.aptoenq_a', '[class*="aptoenq_a"]'];
    let answerDivs: NodeListOf<Element> | null = null;

    for (const selector of answerSelectors) {
      answerDivs = element.querySelectorAll(selector);
      if (answerDivs.length > 0) {
        console.log(`質問${questionIndex}: セレクタ "${selector}" で回答欄を発見 (${answerDivs.length}個)`);
        break;
      }
    }

    if (answerDivs && answerDivs.length > 0) {
      answerDivs.forEach((answerDiv, index) => {
        const htmlElement = answerDiv as HTMLElement;
        htmlElement.style.display = 'none';
        htmlElement.style.visibility = 'hidden';
        htmlElement.style.height = '0';
        htmlElement.style.overflow = 'hidden';
        htmlElement.style.padding = '0';
        htmlElement.style.margin = '0';
        console.log(`質問${questionIndex}の回答欄${index}を非表示にしました`);
      });
    } else {
      console.log(`質問${questionIndex}: 回答欄が見つかりませんでした`);
    }

    // 質問タイトルのスタイルを調整（questionTitleとquestionTitleNoAnsの両方に対応）
    const titleSelectors = ['.questionTitle', '.questionTitleNoAns', 'div.questionTitle', 'div.questionTitleNoAns', '[class*="questionTitle"]'];
    let titleDivs: NodeListOf<Element> | null = null;

    for (const selector of titleSelectors) {
      titleDivs = element.querySelectorAll(selector);
      if (titleDivs.length > 0) {
        console.log(`質問${questionIndex}: セレクタ "${selector}" でタイトルを発見 (${titleDivs.length}個)`);
        break;
      }
    }

    if (titleDivs && titleDivs.length > 0) {
      titleDivs.forEach((titleDiv, index) => {
        const htmlElement = titleDiv as HTMLElement;
        // questionTitleと同じスタイルを適用
        htmlElement.style.backgroundColor = '#e6f7ff';
        htmlElement.style.color = 'rgba(0, 0, 0, 0.85)';
        htmlElement.style.fontWeight = 'bold';
        htmlElement.style.padding = '0.75rem 1rem';
        htmlElement.style.borderBottom = 'none';
        htmlElement.style.borderRadius = '6px';
        console.log(`質問${questionIndex}のタイトル${index}のスタイルを調整しました`);
      });
    } else {
      console.log(`質問${questionIndex}: タイトルが見つかりませんでした`);
    }
  }

  /**
   * 回答欄なしタイプ（見出しのみ）の質問かどうかを判定
   * 条件: questionType=6（回答なし設問専用タイプ）のみ
   */
  private isHeadingOnlyQuestion(questionIndex: number): boolean {
    const question = this.question[questionIndex];
    if (!question) return false;

    console.log(`質問${questionIndex}の判定開始:`, {
      questionType: question.questionType,
      branchMap: question.branchMap,
      questionContent: question.questionContent
    });

    // questionType=6の場合のみ回答なし設問
    if (question.questionType?.toString() === '6') {
      console.log(`質問${questionIndex}: questionType=6 のため回答なし設問と判定`);
      return true;
    }

    console.log(`質問${questionIndex}: questionType=${question.questionType} のため通常の設問と判定`);
    return false;
  }
  
  /**
   * CSSを適用する
   */
  private applyCss() {
    if (!this.css || !this.aptoenq) return;
    
    // CSSルールを適用
    this.css.forEach(css => {
      if (css && css.length >= 2) {
        const elements = this.aptoenq?.querySelectorAll(css[0]);
        if (elements && elements.length > 0) {
          elements.forEach(el => {
            (el as HTMLElement).setAttribute('style', css[1]);
          });
        }
      }
    });
    
    // セレクトボックスのラッパー追加
    const selects = this.aptoenq.querySelectorAll('.aptoenq_qa select:not(.addedDiv)');
    selects.forEach(select => {
      const wrapper = document.createElement('div');
      wrapper.className = 'selectBox';
      select.parentNode?.insertBefore(wrapper, select);
      wrapper.appendChild(select);
      select.classList.add('addedDiv');
    });
    
    // ダミー要素を非表示
    const dummies = this.aptoenq.querySelectorAll('.aptoenq_dummy');
    dummies.forEach(dummy => {
      (dummy as HTMLElement).style.display = 'none';
    });
    
    // ツールチップ位置の調整を追加
    this.setupTooltipPositioning();
  }
  
  // ツールチップの位置調整処理
  private setupTooltipPositioning() {
    if (!this.aptoenq) return;
    
    // 各ツールチップ要素にマウスイベントを追加
    const tooltips = this.aptoenq.querySelectorAll('.enqtooltip');
    
    tooltips.forEach(tooltipEl => {
      const tooltip = tooltipEl as HTMLElement;
      const description = tooltip.querySelector('.description') as HTMLElement;
      
      if (!description) return;
      
      // マウスオーバー時
      tooltip.addEventListener('mouseenter', () => {
        // 現在の要素の位置を取得
        const tooltipRect = tooltip.getBoundingClientRect();
        
        // 吹き出しの位置を計算
        const top = tooltipRect.top - description.offsetHeight - 20;
        const left = tooltipRect.left;
        
        // 画面外にならないように調整
        const adjustedTop = Math.max(10, top);
        const adjustedLeft = Math.max(10, Math.min(left, window.innerWidth - description.offsetWidth - 10));
        
        // 吹き出しの位置を設定
        description.style.position = 'fixed';
        description.style.top = adjustedTop + 'px';
        description.style.left = adjustedLeft + 'px';
        description.style.zIndex = '9999';
      });
      
      // 一応クリーンアップ処理も追加
      tooltip.addEventListener('mouseleave', () => {
        description.style.top = '';
        description.style.left = '';
      });
    });
  }

  /**
   * Enable or disable an element
   */
  enabled(o: HTMLElement) {
    o.removeAttribute("disabled");
  }

  disabled(o: HTMLElement) {
    o.setAttribute("disabled", "disabled");
  }

  /**
   * Handle state changes when a form element changes
   */
  /**
   * 指定された質問が分岐なしタイプかどうかをチェック
   * 分岐なしとは:
   * 1. 自由テキスト(questionType=4)
   * 2. branchMapがないか空
   * 3. branchMapのすべての値がnull/空
   * 4. チェックボックス(questionType=2)は特殊で次の質問を常に表示
   */
  private isNonBranchingQuestionType(questionNo: number): boolean {
    const question = this.question[questionNo];
    if (!question) return false;
    
    // チェックボックス(questionType=2)は特殊ケースで、次の質問を表示する
    // APIのbranchMapがあっても、複数選択可能なので次の質問も表示する
    if (question.questionType === '2' || question.questionType?.toString() === '2') {
      return true;
    }
    
    // 自由テキスト(questionType=4)は分岐なし
    if (question.questionType === '4' || question.questionType?.toString() === '4') {
      return true;
    }
    
    // branchMapがないか空の場合も分岐なし
    if (!question.branchMap || Object.keys(question.branchMap).length === 0) {
      return true;
    }
    
    // branchMapのすべての値がnullまたは空文字の場合も分岐なし
    if (question.branchMap) {
      const branchMap = question.branchMap;
      const hasNonNull = Object.keys(branchMap).some(key => {
        const value = branchMap[key];
        return value !== null && value !== undefined && value !== "";
      });
      if (!hasNonNull) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * 質問の初期選択値を取得する
   */
  private getInitialSelectedValueFromQuestion(question: QuestionItem): string | null {
    if (!question.questionHtml) return null;
    
    const questionHtml = question.questionHtml;
    
    // セレクトボックスの選択値を探す - 正規表現を強化
    // 複数の正規表現パターンを試す - 順序は重要
    
    // パターン1: value=の前にselectedがある場合
    const selectedPattern1 = questionHtml.match(/option[^>]*selected[^>]*value=["']([^"']+)["']/i);
    if (selectedPattern1 && selectedPattern1[1]) {
      return selectedPattern1[1];
    }
    
    // パターン2: value=の後にselectedがある場合
    const selectedPattern2 = questionHtml.match(/option[^>]*value=["']([^"']+)["'][^>]*selected/i);
    if (selectedPattern2 && selectedPattern2[1]) {
      return selectedPattern2[1];
    }
    
    // HTMLのパースを試みる - 大規模な文字列ではパフォーマンス影響がある可能性
    if (questionHtml.includes('selected') && questionHtml.includes('option')) {
      // DOMパーサーでHTMLを解析
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = questionHtml;
      
      // 選択されたオプションを探す
      const selectedOption = tempDiv.querySelector('option[selected]');
      if (selectedOption && selectedOption.hasAttribute('value')) {
        const value = selectedOption.getAttribute('value');
        if (value) {
          return value;
        }
      }
    }
    
    // ラジオボタンやチェックボックスの選択値を探す - 同様に強化
    const checkedPattern1 = questionHtml.match(/input[^>]*checked[^>]*value=["']([^"']+)["']/i);
    if (checkedPattern1 && checkedPattern1[1]) {
      return checkedPattern1[1];
    }
    
    const checkedPattern2 = questionHtml.match(/input[^>]*value=["']([^"']+)["'][^>]*checked/i);
    if (checkedPattern2 && checkedPattern2[1]) {
      return checkedPattern2[1];
    }
    
    // 初期選択値があるはずなのに見つからなかった場合も処理は継続
    if (questionHtml.includes('selected') || questionHtml.includes('checked')) {
      // 値が取得できなかった
    }
    
    return null;
  }

  /**
   * DOM上の実際の選択値を取得（ユーザー操作を反映）
   * 静的なquestionHtmlではなく、実際のDOM状態から選択値を取得する
   */
  private getCurrentSelectedValueFromDOM(questionIndex: number): string | null {
    if (!this.aptoenq) return null;

    const questionElement = this.aptoenq.querySelector(`.aptoenq_qa[quesNo="${questionIndex}"]`) as HTMLElement;
    if (!questionElement) return null;

    // ラジオボタンの選択値を取得
    const checkedRadio = questionElement.querySelector('input[type="radio"]:checked') as HTMLInputElement;
    if (checkedRadio && checkedRadio.value) {
      return checkedRadio.value;
    }

    // チェックボックスの選択値を取得（複数の場合は最初の値を返す）
    // ※分岐判定では最初の選択値のみを使用する想定
    const checkedCheckbox = questionElement.querySelector('input[type="checkbox"]:checked') as HTMLInputElement;
    if (checkedCheckbox && checkedCheckbox.value) {
      return checkedCheckbox.value;
    }

    // セレクトボックスの選択値を取得
    const select = questionElement.querySelector('select') as HTMLSelectElement;
    if (select && select.selectedIndex > 0 && select.value) {
      return select.value;
    }

    return null;
  }

  /**
   * DOM上で選択済みかどうかをチェック（ユーザー操作を反映）
   * 静的なquestionHtmlではなく、実際のDOM状態から判定する
   */
  private hasCurrentSelectionInDOM(questionIndex: number): boolean {
    return this.getCurrentSelectedValueFromDOM(questionIndex) !== null;
  }

  /**
   * 質問と選択値に基づいて、直接の分岐先質問のインデックスを取得
   */
  private getDirectNextQuestionIndex(questionNo: number, selectedValue: string): number | null {
    if (questionNo < 0 || questionNo >= this.question.length || !selectedValue) {
      return null;
    }
    
    const question = this.question[questionNo];
    
    // 分岐マップがある場合
    if (question.branchMap) {
      const branchMap = question.branchMap;
      
      // 選択値に対応する分岐先があるか確認
      if (Object.prototype.hasOwnProperty.call(branchMap, selectedValue)) {
        const branchValue = branchMap[selectedValue];
        
        // 分岐先がnullまたは空の場合は次の質問へ
        if (branchValue === null || branchValue === "") {
          // nextプロパティがあればそれを使用
          if (question.next && question.next !== "") {
            return parseInt(String(question.next), 10) - 1;
          }
          // なければ次のインデックス
          return questionNo + 1;
        }
        
        // 分岐先が指定されている場合
        if (branchValue !== null && branchValue !== undefined) {
          return parseInt(String(branchValue), 10) - 1;
        }
      }
    }
    
    // nextプロパティがあれば使用
    if (question.next && question.next !== "") {
      return parseInt(String(question.next), 10) - 1;
    }
    
    // デフォルトでは次の質問
    return questionNo + 1;
  }
  
  /**
   * APIレスポンスの構造に基づいて関連する質問を見つける
   * 特定の文字列に依存せず、データ構造や質問間の関係性から判断する
   */
  private findRelatedQuestions(questionNo: number, selectedValue: string): number[] {
    const result: number[] = [];
    
    // APIレスポンスのbranchMap値から関連性を判断
    const currentQuestion = this.question[questionNo];
    
    // 次の質問番号を決定（基本的な連続性）
    let nextQuestionIndex = questionNo + 1;
    if (currentQuestion && currentQuestion.next && currentQuestion.next !== "") {
      nextQuestionIndex = parseInt(String(currentQuestion.next), 10) - 1;
    }
    
    // 選択値に対応するbranchMap値がある場合
    if (currentQuestion && currentQuestion.branchMap && 
        Object.prototype.hasOwnProperty.call(currentQuestion.branchMap, selectedValue)) {
      const branchValue = currentQuestion.branchMap[selectedValue];
      
      if (branchValue === null || branchValue === "") {
        // 分岐値がnullの場合は次の質問を表示（連続表示と初期選択は別メソッドで処理）
        if (nextQuestionIndex >= 0 && nextQuestionIndex < this.question.length && 
            nextQuestionIndex > questionNo) {
          result.push(nextQuestionIndex);
        }
      } else {
        // 分岐先が指定されている場合は、その質問を返す
        try {
          const targetIndex = parseInt(String(branchValue), 10) - 1;
          if (targetIndex >= 0 && targetIndex < this.question.length) {
            result.push(targetIndex);
            return result; // 明示的な分岐先が指定されている場合は他の質問は表示しない
          }
        } catch (e) {
          // Invalid branch value
        }
      }
    } else {
      // branchMapがない、または選択値に対応する分岐がない場合は次の質問を表示
      if (nextQuestionIndex >= 0 && nextQuestionIndex < this.question.length && 
          nextQuestionIndex > questionNo) {
        result.push(nextQuestionIndex);
      }
    }
    
    return result;
  }
  
  /**
   * 質問間の関係性から、潜在的な分岐先かどうかを判断
   */
  private isPotentialBranchTarget(sourceIndex: number, targetIndex: number): boolean {
    // 基本のチェック
    if (sourceIndex >= targetIndex) return false;
    if (sourceIndex < 0 || targetIndex >= this.question.length) return false;
    
    const sourceQuestion = this.question[sourceIndex];
    const targetQuestion = this.question[targetIndex];
    
    // 分岐元の質問がbranchMapを持っていない場合はfalse
    if (!sourceQuestion.branchMap) return false;
    
    // 分岐構造から関連性を調査
    // 例：設問3でOK/NGの分岐があり、設問5がNG選択時の目的地である場合
    // この関係性は直接のbranchMapではなく、構造的な関連性から判断
    
    // branchMapの値を調査
    const branchMapValues = Object.values(sourceQuestion.branchMap).filter(v => v !== null && v !== "");
    if (branchMapValues.length === 0) return false;
    
    // 同じグループの質問かどうか
    // 例：設問3と設問5が同じグループ（NGに関する質問群）に属している
    const isInSameQuestionGroup = 
      // インデックスが近い
      (targetIndex - sourceIndex) <= 3 && 
      // 間に分岐ありの質問が少ない
      this.countBranchingQuestionsInRange(sourceIndex + 1, targetIndex) <= 1;
    
    return isInSameQuestionGroup;
  }
  
  /**
   * 指定範囲に含まれる分岐ありの質問数を数える
   */
  private countBranchingQuestionsInRange(startIndex: number, endIndex: number): number {
    let count = 0;
    
    for (let i = startIndex; i < endIndex; i++) {
      if (i < 0 || i >= this.question.length) continue;
      
      const question = this.question[i];
      // 分岐なしならスキップ
      if (this.isNonBranchingQuestionType(i)) continue;
      
      // 分岐あり設問をカウント
      count++;
    }
    
    return count;
  }
  
  /**
   * 指定された質問の初期選択値に基づいて表示すべき分岐先質問を処理
   */
  private processInitialSelectionOfQuestion(questionIndex: number, allQuestions: NodeListOf<Element>) {
    if (questionIndex < 0 || questionIndex >= this.question.length) return;
    
    const question = this.question[questionIndex];
    if (!question) return;
    
    const initialSelectedValue = this.getInitialSelectedValueFromQuestion(question);
    
    if (!initialSelectedValue) {
      // 初期選択値がなくても、テキスト入力（questionType=4）でnextプロパティがあるか確認
      if (question.questionType !== undefined && 
          (question.questionType === '4' || question.questionType.toString() === '4') && 
          question.next && question.next !== "") {
        // nextプロパティがある場合は、その指定先を表示
        const nextIndex = parseInt(String(question.next), 10) - 1;
        if (nextIndex >= 0 && nextIndex < allQuestions.length) {
          (allQuestions[nextIndex] as HTMLElement).style.display = '';
          
          // 連鎖的に処理（次の質問のnextや初期選択も処理）
          this.processInitialSelectionOfQuestion(nextIndex, allQuestions);
        }
      }
      return;
    }
    
    
    // 関連する質問を表示
    const relatedQuestions = this.findRelatedQuestions(questionIndex, initialSelectedValue);
    
    // 関連質問を表示
    relatedQuestions.forEach(index => {
      if (index > questionIndex && index < allQuestions.length) {
        (allQuestions[index] as HTMLElement).style.display = '';
        
        // 連鎖表示の処理 - 表示した質問も再帰的に処理
        this.processChainedQuestionsDisplay(index, allQuestions);
        
        // 追加: テキスト入力（questionType=4）でnextプロパティがあるか確認
        const targetQuestion = this.question[index];
        if (targetQuestion && targetQuestion.questionType !== undefined && 
            (targetQuestion.questionType === '4' || targetQuestion.questionType.toString() === '4') && 
            targetQuestion.next && targetQuestion.next !== "") {
          // nextプロパティがある場合は、その指定先も表示
          const nextIndex = parseInt(String(targetQuestion.next), 10) - 1;
          if (nextIndex >= 0 && nextIndex < allQuestions.length) {
            (allQuestions[nextIndex] as HTMLElement).style.display = '';
            
            // 連鎖的に処理（次の質問のnextや初期選択も処理）
            this.processInitialSelectionOfQuestion(nextIndex, allQuestions);
          }
        }
      }
    });
  }
  
  /**
   * 分岐設定に従って、実際に表示すべき次の質問インデックスを取得する
   * propsのnextやbranchMapに基づいて適切な分岐先を決定する
   */
  private getNextQuestionIndex(currentQuestionNo: number, selectedValue: string | null = null): number | null {
    if (currentQuestionNo < 0 || currentQuestionNo >= this.question.length) {
      return null;
    }
    
    const currentQuestion = this.question[currentQuestionNo];
    
    // 1. 選択された値に対応するbranchMapの値を確認
    if (selectedValue && currentQuestion.branchMap) {
      // 安全なプロパティアクセスを確保
      const branchMap = currentQuestion.branchMap;
      if (Object.prototype.hasOwnProperty.call(branchMap, selectedValue)) {
        const branchValue = branchMap[selectedValue];
        
        // branchValueがnullの場合は分岐なし（次の質問へ進む）
        if (branchValue === null || branchValue === "") {
          // nextが指定されていればそれを使用、そうでなければ現在の質問番号+1
          if (currentQuestion.next) {
            return parseInt(String(currentQuestion.next), 10) - 1;
          } else {
            return currentQuestionNo + 1;
          }
        }
        
        // 分岐先が指定されている場合（数値に変換して返す）
        if (branchValue !== null) {
          return parseInt(String(branchValue), 10) - 1;
        }
      }
    }
    
    // 2. branchMapに依存しない次の質問の指定（next）があれば使用
    if (currentQuestion.next) {
      return parseInt(String(currentQuestion.next), 10) - 1;
    }
    
    // 3. デフォルトでは次の質問（現在のインデックス+1）を返す
    return currentQuestionNo + 1;
  }
  
  /**
   * 初期表示すべき設問のindexリストを返す
   * サンプルのState.prototype.showNext実装に倣い、APIレスポンスを正確に解析する
   */
  private getInitialDisplayQuestions(): number[] {
    // 表示する質問インデックスリスト
    const displayIndices: number[] = [];

    if (this.question.length === 0) {
      return displayIndices; // 質問がない場合は空配列を返す
    }

    // 処理済みの質問を追跡（無限ループ防止）
    const processedIndices = new Set<number>();

    // 最初の質問から連鎖的に表示すべき質問を解析
    this.analyzeAndAddQuestionChain(0, displayIndices, processedIndices);

    console.log('初期表示する質問のインデックス:', displayIndices);

    return displayIndices;
  }
  
  /**
   * 連鎖的に表示すべき質問を追加
   */
  private addChainedQuestions(startIndex: number, displayIndices: number[]): void {
    const question = this.question[startIndex];
    if (!question) return;
    
    // テキスト入力（questionType=4）でnextプロパティがある場合は特別処理
    if (question.questionType !== undefined && 
        (question.questionType === '4' || question.questionType.toString() === '4') && 
        question.next && question.next !== "") {
      try {
        // next値を計算
        const nextIndex = parseInt(String(question.next), 10) - 1;
        if (nextIndex >= 0 && nextIndex < this.question.length && 
            !displayIndices.includes(nextIndex)) {
          displayIndices.push(nextIndex);
          
          // 連鎖的に処理（無限ループを防ぐためにチェック）
          if (nextIndex !== startIndex) {
            this.addChainedQuestions(nextIndex, displayIndices);
          }
        }
      } catch (e) {
        // Next値の連鎖処理中にエラー
      }
    }
  }
  
  /**
   * 質問の連鎖を解析し、表示すべき質問をインデックスリストに追加する
   * 再帰的に次の質問も処理し、連鎖的に表示すべき質問を特定する
   */
  private analyzeAndAddQuestionChain(
    startIndex: number,
    displayIndices: number[],
    processedIndices: Set<number>
  ): void {
    // 無効な質問インデックスや処理済みの質問はスキップ
    if (startIndex < 0 || startIndex >= this.question.length || processedIndices.has(startIndex)) {
      return;
    }
    
    // 現在の質問を表示対象に追加し、処理済みマーク
    displayIndices.push(startIndex);
    processedIndices.add(startIndex);
    
    const question = this.question[startIndex];
    const questionType = question.questionType?.toString();
    
    
    // 次に検討する質問インデックス（デフォルトは連番の次）
    let nextIndex: number = startIndex + 1;
    
    // API指定の次の質問があればそれを優先
    if (question.next && question.next !== "") {
      nextIndex = parseInt(String(question.next), 10) - 1;
    }
    
    // 初期選択値と分岐の確認
    const hasSelection = this.hasInitialSelection(startIndex);
    
    // 質問タイプと初期選択に基づいて分岐処理
    switch(questionType) {
      case '1': // ラジオボタン
        if (hasSelection) {
          // 初期選択値がある場合
          const selectedValue = this.getInitialSelectedValueFromQuestion(question);
          
          if (selectedValue && question.branchMap && 
              Object.prototype.hasOwnProperty.call(question.branchMap, selectedValue)) {
            // 選択値に対応する分岐先がある
            const branchValue = question.branchMap[selectedValue];
            
            if (branchValue !== null && branchValue !== "" && branchValue !== undefined) {
              // 明示的な分岐先が指定されている
              nextIndex = parseInt(String(branchValue), 10) - 1;
            }
          }
        } else {
          // 初期選択がなく分岐ありの場合は、表示終了（次の質問は表示しない）
          if (this.hasBranchingWithNonNullValues(startIndex)) {
            return;
          }
        }
        break;
        
      case '2': // チェックボックス
        // チェックボックスは常に次の質問を表示（分岐があっても）
        break;
        
      case '3': // セレクトボックス
        if (hasSelection) {
          // 初期選択値がある場合
          const selectedValue = this.getInitialSelectedValueFromQuestion(question);

          if (selectedValue && question.branchMap &&
              Object.prototype.hasOwnProperty.call(question.branchMap, selectedValue)) {
            // 選択値に対応する分岐先がある
            const branchValue = question.branchMap[selectedValue];

            if (branchValue !== null && branchValue !== "" && branchValue !== undefined) {
              // 明示的な分岐先が指定されている
              nextIndex = parseInt(String(branchValue), 10) - 1;
            }
          }
        } else {
          // 初期選択がなく分岐ありの場合は、表示終了（次の質問は表示しない）
          if (this.hasBranchingWithNonNullValues(startIndex)) {
            return;
          }
        }
        break;
        
      case '4': // 自由テキスト入力
        // nextプロパティがある場合はそれを使用する
        if (question.next && question.next !== "") {
          // next値が指定されている場合は特別処理
          const nextValue = parseInt(String(question.next), 10) - 1;
          if (nextValue >= 0 && nextValue < this.question.length) {
            nextIndex = nextValue;
          }
        } else {
          // 自由テキストは常に次の質問を表示（nextがない場合は連番の次）
        }
        break;
        
      case '5': // 添付ファイル
        // 添付ファイルは特殊処理なし（次の質問へ）
        break;

      case '6': // 回答なし設問（見出しのみ）
        // 回答なし設問は常に次の質問を表示
        break;

      default:
        // 不明なタイプの処理（デフォルトでは次の質問へ）
        break;
    }
    
    // 添付ファイル質問をスキップ
    while (nextIndex < this.question.length && 
           this.question[nextIndex]?.questionType?.toString() === '5') {
      nextIndex++;
    }
    
    // 次の質問が有効範囲内かつ未処理の場合は連鎖処理
    if (nextIndex < this.question.length && !processedIndices.has(nextIndex)) {
      this.analyzeAndAddQuestionChain(nextIndex, displayIndices, processedIndices);
    }
  }
  
  /**
   * 質問に分岐があり、その中にnullでない値があるかをチェック
   */
  private hasBranchingWithNonNullValues(questionIndex: number): boolean {
    const question = this.question[questionIndex];
    if (!question || !question.branchMap) return false;
    
    const branchMap = question.branchMap;
    for (const key in branchMap) {
      if (Object.prototype.hasOwnProperty.call(branchMap, key)) {
        const value = branchMap[key];
        if (value !== null && value !== undefined && value !== "") {
          return true;
        }
      }
    }
    
    return false;
  }
  
  /**
   * 質問が初期選択値を持っているかをチェック
   */
  private hasInitialSelection(questionIndex: number): boolean {
    const question = this.question[questionIndex];
    if (!question || !question.questionHtml) return false;
    
    // セレクトボックスの選択値をチェック
    const selectedOption = question.questionHtml.match(/option[^>]*selected[^>]/i);
    if (selectedOption) {
      return true;
    }
    
    // ラジオボタンやチェックボックスの選択値をチェック
    const checkedInput = question.questionHtml.match(/input[^>]*checked[^>]/i);
    if (checkedInput) {
      return true;
    }
    
    return false;
  }
  
  /**
   * 分岐なしの質問タイプで、連続する質問を表示
   */
  private showSequentialQuestions(startQuestionNo: number) {
    const allQuestions = this.aptoenq?.querySelectorAll('.aptoenq_qa');
    if (!allQuestions || allQuestions.length === 0) return;
    
    // 開始質問より後の質問を一旦すべて非表示
    for (let i = startQuestionNo + 1; i < allQuestions.length; i++) {
      (allQuestions[i] as HTMLElement).style.display = 'none';
    }
    
    // 直後の質問を表示（1つだけ）- まずは基本処理
    if (startQuestionNo + 1 < allQuestions.length) {
      const nextQuestion = (allQuestions[startQuestionNo + 1] as HTMLElement);
      nextQuestion.style.display = '';
      
      
      // 次に表示する質問の初期選択値に基づいて、さらに次の質問の表示を判断
      this.processChainedQuestionsDisplay(startQuestionNo + 1, allQuestions);
    }
  }
  
  /**
   * 質問の初期選択状態に基づいて、後続の質問を連鎖的に表示するかどうかを判断
   * サンプル実装のshowNext()に相当する処理を参考に実装
   * 複数の質問が連続して初期選択された場合の連鎖表示に対応
   */
  private processChainedQuestionsDisplay(questionNo: number, allQuestions: NodeListOf<Element>) {
    if (questionNo < 0 || questionNo >= this.question.length) return;
    
    // 現在の質問から連鎖的に表示するものを見つける
    // 処理中の質問を追跡してループを防止
    const processedQuestions = new Set<number>();
    let currentIndex = questionNo;
    
    // 再帰処理はスタックオーバーフローの可能性があるため、
    // while文で連鎖処理を実装
    while (currentIndex < this.question.length && !processedQuestions.has(currentIndex)) {
      processedQuestions.add(currentIndex);
      const currentQuestion = this.question[currentIndex];
      const questionType = currentQuestion.questionType?.toString();
      
      // 次に処理する質問のインデックス（デフォルトは次の連番）
      let nextIndex = currentIndex + 1;
      
      // API指定の次の質問があればそれを優先
      if (currentQuestion.next && currentQuestion.next !== "") {
        nextIndex = parseInt(String(currentQuestion.next), 10) - 1;
      }
      
      // 選択値と分岐確認
      // まずDOM上の実際の選択値をチェック（ユーザー操作を反映）
      const domSelectedValue = this.getCurrentSelectedValueFromDOM(currentIndex);
      const hasCurrentSelection = domSelectedValue !== null;

      // フォールバック: DOM上に選択がない場合は初期選択値をチェック
      const hasInitialSelectionValue = !hasCurrentSelection && this.hasInitialSelection(currentIndex);
      const hasSelection = hasCurrentSelection || hasInitialSelectionValue;

      // 質問タイプ別の処理
      if (questionType === '2') { // チェックボックス
        // チェックボックスは常に次の質問を表示（分岐があっても）
      }
      else if (questionType === '4') { // 自由テキスト入力
        // 自由テキストは常に次の質問を表示
      }
      else if (hasSelection) { // 選択がある場合の処理
        // 選択値を取得（DOM優先、なければ初期選択値）
        const selectedValue = domSelectedValue || this.getInitialSelectedValueFromQuestion(currentQuestion);

        if (selectedValue && currentQuestion.branchMap &&
            Object.prototype.hasOwnProperty.call(currentQuestion.branchMap, selectedValue)) {
          // 選択値に対応する分岐先がある
          const branchValue = currentQuestion.branchMap[selectedValue];

          if (branchValue !== null && branchValue !== "" && branchValue !== undefined) {
            // 明示的な分岐先がある場合はそこへ
            nextIndex = parseInt(String(branchValue), 10) - 1;
          } else {
            // 分岐先がnullなので次の質問へ
          }
        } else {
          // 分岐設定がないため次の質問へ
        }
      }
      else if ((questionType === '1' || questionType === '3') && this.hasBranchingWithNonNullValues(currentIndex)) {
        // ラジオボタン・セレクトボックスで選択なし、分岐ありの場合は表示終了
        break;
      }
      else {
        // その他のケースは次の質問へ
      }
      
      // 添付ファイルをスキップ
      while (nextIndex < this.question.length && 
             this.question[nextIndex]?.questionType?.toString() === '5') {
        nextIndex++;
      }
      
      // 次の質問が有効かどうか確認
      if (nextIndex >= 0 && nextIndex < this.question.length && nextIndex < allQuestions.length) {
        // 質問を表示
        (allQuestions[nextIndex] as HTMLElement).style.display = '';
        
        // 次の質問を処理
        currentIndex = nextIndex;
      } else {
        // 有効な次の質問がなければ終了
        break;
      }
    }
  }
  
  changed(o: FormElement, no: number) {
    
    // Handle select options
    if (o.name) {
      const oS = document.getElementsByName(o.name);
      const inNum = oS.length;
      
      for (let i = 1; i <= inNum; i++) {
        const stSel = o.name + 'sel' + i;
        const oSel = document.getElementsByName(stSel);
        
        if (oSel.length === 1) {
          // Check if this option is selected
          if ((oS[i - 1] as HTMLInputElement).checked === true) {
            this.enabled(oSel[0] as HTMLElement);
          } else {
            this.disabled(oSel[0] as HTMLElement);
          }
        }
      }
    }
    
    // Handle 'other' options
    if (o.name) {
      const stOther = o.name + 'other';
      const oOther = document.getElementsByName(stOther);
      
      if (oOther.length === 1) {
        // name属性を持つかチェック（HTMLInputElementまたはHTMLSelectElementに型アサーション）
        const otherElement = oOther[0] as HTMLInputElement | HTMLSelectElement;
        if ('name' in otherElement && otherElement.name) {
          // Handle based on input type
          if (o.type === "radio") {
            // For radio buttons
            const otherRadio = (otherElement as HTMLElement).closest('label')?.querySelector('input[type="radio"]');
            if (o === otherRadio) {
              this.enabled(otherElement as HTMLElement);
            } else {
              this.disabled(otherElement as HTMLElement);
            }
          } else if (o.type === "checkbox") {
            // For checkboxes
            const otherCheckbox = (otherElement as HTMLElement).closest('label')?.querySelector('input[type="checkbox"]');
            if (otherCheckbox && (otherCheckbox as HTMLInputElement).checked) {
              this.enabled(otherElement as HTMLElement);
            } else {
              this.disabled(otherElement as HTMLElement);
            }
          } else if (o.tagName && o.tagName.toLowerCase() === 'select') {
            // For select elements
            const selectElement = o as HTMLSelectElement; // 明示的にHTMLSelectElementとして扱う
            const otherSelect = document.getElementById(stOther + 'in');
            if (otherSelect) {
              if (selectElement.selectedIndex === (selectElement.options.length - 1)) {
                otherSelect.style.display = '';
              } else {
                otherSelect.style.display = 'none';
              }
            }
          }
        }
      }
    }

    // 全ての質問要素を取得（表示/非表示の制御用）
    const allQuestions = this.aptoenq?.querySelectorAll('.aptoenq_qa');
    if (!allQuestions || allQuestions.length === 0) {
      return;
    }
    
    // 選択値に基づいた分岐処理
    
    // 入力値が存在するか（チェックボックスやセレクトボックスの特殊ケースを考慮）
    const hasValue = 
      !!o.value || 
      (o.tagName && o.tagName.toLowerCase() === 'select' && (o as HTMLSelectElement).selectedIndex > 0) || 
      (o.type === 'checkbox' && (o as HTMLInputElement).checked);
    
    // セレクトボックスの場合、特に空の値（「ご選択ください」など）を選んだ場合
    if (o.tagName && o.tagName.toLowerCase() === 'select' && !o.value) {
      
      // 現在の質問より後の質問をすべて非表示にする
      if (allQuestions) {
        for (let i = no + 1; i < allQuestions.length; i++) {
          (allQuestions[i] as HTMLElement).style.display = 'none';
        }
      }
      
      return;
    }
    
    // 他の入力形式で値がない場合
    if (!hasValue) {
      return;
    }
    
    // 選択した値を保存（設問の関連を調べるため）
    const selectedValue = o.value;
    
    // 最初に、現在の質問より後の質問をすべて非表示にする
    for (let i = no + 1; i < allQuestions.length; i++) {
      (allQuestions[i] as HTMLElement).style.display = 'none';
    }
    
    // 分岐処理（APIレスポンスの構造に基づいて次に表示する質問を決定）
    if (this.question[no].branchMap) {
      // 分岐マップがある場合
      const branchMap = this.question[no].branchMap;
      
      if (selectedValue && Object.prototype.hasOwnProperty.call(branchMap, selectedValue)) {
        // 選択した値に対応する分岐情報がある場合
        // branchMapが未定義である可能性をTypeScriptに理解させるため再確認
        const safebranchMap = branchMap as Record<string, string | number | null>;
        const branchValue = safebranchMap[selectedValue];
        
        if (branchValue === null || branchValue === "") {
          // nullの場合は次の質問へ（連続性のある次の質問のみ）
          this.showSequentialQuestions(no);
        } else {
          // 具体的な分岐先がある場合
          try {
            const targetIndex = parseInt(String(branchValue), 10) - 1;
            if (targetIndex >= 0 && targetIndex < allQuestions.length) {
              (allQuestions[targetIndex] as HTMLElement).style.display = '';

              // 連鎖的に質問を表示
              // processChainedQuestionsDisplayがDOM状態を参照するため、
              // ここで初期選択値を取得する必要はない
              this.processChainedQuestionsDisplay(targetIndex, allQuestions);
            }
          } catch (e) {
            // Error processing branch value
          }
        }
      } else {
        // 選択値に対応する分岐情報がない場合、次の質問のみ表示
        this.showSequentialQuestions(no);
      }
    } else {
      // 分岐マップがない場合も次の質問のみ表示
      this.showSequentialQuestions(no);
    }
    
    // 特定のケースで選択値に基づく関連質問を表示
    // ここでは直接表示する質問はshowSequentialQuestions内で処理しているため不要
    // this.showRelatedQuestionsFor(no, selectedValue, allQuestions);
  }
  
  /**
   * 選択値に対応する次の質問を表示する
   * APIのbranchMap構造に基づいて表示すべき質問を決定
   */
  private showRelatedQuestionsFor(questionNo: number, selectedValue: string, allQuestions: NodeListOf<Element>) {
    if (!selectedValue) return;
    
    // 選択値に基づく次の質問を見つける
    const relatedQuestions = this.findRelatedQuestions(questionNo, selectedValue);
    
    // 次の質問を表示（分岐先または次の質問）
    relatedQuestions.forEach(index => {
      if (index > questionNo && index < allQuestions.length) {
        (allQuestions[index] as HTMLElement).style.display = '';
        
        // この質問がさらに選択済み値を持っている場合はその分岐も処理
        this.processInitialSelectionOfQuestion(index, allQuestions);
      }
    });
  }
  
  /**
   * 初期選択肢がある質問を処理し、表示するべき関連質問を表示する
   */
  private processInitialSelections() {
    if (!this.aptoenq) return;
    
    // 全ての質問を取得
    const allQuestions = this.aptoenq.querySelectorAll('.aptoenq_qa');
    if (!allQuestions || allQuestions.length === 0) return;
    
    
    // 初期選択肢を持つ質問を処理
    for (let i = 0; i < this.question.length; i++) {
      // 表示されている質問のみ処理
      const questionElement = this.aptoenq.querySelector(`.aptoenq_qa[quesNo="${i}"]`) as HTMLElement;
      if (!questionElement || questionElement.style.display === 'none') continue;
      
      // 質問から初期選択値を取得
      const question = this.question[i];
      const initialValue = this.getInitialSelectedValueFromQuestion(question);
      
      if (initialValue) {
        
        // 関連する質問を表示
        this.showRelatedQuestionsFor(i, initialValue, allQuestions);
        
        // 選択肢がセレクトボックスか確認し、選択肢に対応するさらなる分岐を処理
        const selectElements = questionElement.querySelectorAll('select');
        if (selectElements.length > 0) {
          const selectElement = selectElements[0] as HTMLSelectElement;
          if (selectElement.selectedIndex > 0) {
            // 対応する質問が選択されていることを確認
            const selectedOption = selectElement.options[selectElement.selectedIndex];
            if (selectedOption && selectedOption.value === initialValue) {
              // 初期選択値の分岐先を処理
              if (question.branchMap && Object.prototype.hasOwnProperty.call(question.branchMap, initialValue)) {
                const branchValue = question.branchMap[initialValue];
                
                // nullの場合は次の質問へ
                if (branchValue === null || branchValue === "") {
                  this.showSequentialQuestions(i);
                } 
                // 具体的な分岐先がある場合
                else if (branchValue !== undefined) {
                  try {
                    const targetIndex = parseInt(String(branchValue), 10) - 1;
                    if (targetIndex >= 0 && targetIndex < allQuestions.length) {
                      (allQuestions[targetIndex] as HTMLElement).style.display = '';
                      
                      // 分岐先の初期値も処理
                      this.processInitialSelectionOfQuestion(targetIndex, allQuestions);
                    }
                  } catch (e) {
                    // Error processing branch value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  
  /**
   * 分岐がない場合に次の質問を表示するヘルパーメソッド
   */
  private showNextQuestionWithoutBranch(currentQuestionNo: number, allQuestions: NodeListOf<Element>) {
    // 次の質問を表示
    if (currentQuestionNo + 1 < allQuestions.length) {
      (allQuestions[currentQuestionNo + 1] as HTMLElement).style.display = '';
      
      // 現在の質問がquestionType=4 (自由テキスト)かチェック
      const isTypeTextInput = 
        this.question[currentQuestionNo] && 
        (this.question[currentQuestionNo].questionType === '4' || 
         this.question[currentQuestionNo].questionType?.toString() === '4');
      
      if (isTypeTextInput) {
        // questionType=4の場合は、次の次の質問も表示
        
        // 分岐がない設問タイプの場合は、連続して表示する処理
        let nextQuestionNo = currentQuestionNo + 1;
        while (nextQuestionNo + 1 < allQuestions.length) {
          // 次の質問を取得
          const nextQuestion = this.question[nextQuestionNo];
          
          // 次の質問も分岐がないタイプ（questionType=4など）かチェック
          if (nextQuestion && 
              (nextQuestion.questionType === '4' || 
               nextQuestion.questionType?.toString() === '4' || 
               (nextQuestion.branchMap && Object.keys(nextQuestion.branchMap).length === 0) ||
               !nextQuestion.branchMap)) {
            // 分岐がないタイプなら表示
            (allQuestions[nextQuestionNo + 1] as HTMLElement).style.display = '';
            nextQuestionNo++;
          } else {
            // 分岐があるタイプなら、ここで終了
            break;
          }
        }
      } else {
        // 通常の場合は、次の次以降の質問は非表示
        for (let i = currentQuestionNo + 2; i < allQuestions.length; i++) {
          (allQuestions[i] as HTMLElement).style.display = 'none';
        }
      }
    }
  }

  /**
   * Handle select element changes
   */
  selchanged(o: HTMLSelectElement, no: number) {
    // Handle display of 'other' input for select elements
    const oOther = document.getElementById('ans' + no + 'otherin');
    if (oOther !== null) {
      oOther.style.display = (o.selectedIndex === (o.options.length - 1)) ? 'block' : 'none';
    }
    
    // セレクトボックスの選択変更を受け取ったら、changedメソッドを呼び出して共通処理を実行
    // これにより、選択値に関連する質問も表示されるようになる
    this.changed(o, no);
  }

  /**
   * Display the next questions based on current state
   */
  showNext() {
    // aptoenq要素の再取得を試みる（モーダルの再オープン対応）
    if (!this.aptoenq) {
      this.aptoenq = document.getElementById('aptoenqform');
      
      // 見つからない場合、モーダル内の要素を探す
      if (!this.aptoenq) {
        const modalContent = document.querySelector('.selectEnqueteInputAid .ant-modal-body');
        if (modalContent) {
          // ボーダー付きコンテナを探す
          const container = modalContent.querySelector('[style*="border"]');
          if (container) {
            // 既存の要素を探す
            const existingElement = container.querySelector('#aptoenqform');
            if (existingElement) {
              this.aptoenq = existingElement as HTMLElement;
            } else {
              // 新しい要素を作成
              const newElement = document.createElement('div');
              newElement.id = 'aptoenqform';
              newElement.className = 'fontM';
              
              // 最初の要素として挿入
              container.insertBefore(newElement, container.firstChild);
              this.aptoenq = newElement;
            }
          }
        }
      }
    }
    
    // 再確認
    if (!this.aptoenq) {
      return;
    }
    
    // no が負の数になっている場合は修正する
    if (this.no < 0) {
      this.no = 0;
    }
    
    
    // クリアしてから質問を表示
    try {
      const children = Array.from(this.aptoenq.querySelectorAll('.aptoenq_qa'));
      children.forEach(child => {
        if (this.aptoenq) {
          this.aptoenq.removeChild(child);
        }
      });
    } catch (e) {
      // エラー時はHTMLを直接クリア
      if (this.aptoenq) {
        this.aptoenq.innerHTML = '';
      }
    }
    
    // 初期状態または次のインデックスがない場合は最初の質問を表示
    if ((this.no === 0 && (!this.aptoenq.childNodes.length || this.aptoenq.childNodes.length === 0)) || 
        (this.no >= this.question.length)) {
      this.no = 0; // インデックスをリセット
      this.displaySingleQuestion(0);
      return;
    }
    
    let i: number;
    let next = -1;
    
    // 安全のために長さチェックを追加
    let maxIndex = Math.min(this.num, this.question.length - 1);
    
    for (i = this.no; i <= maxIndex; i++) {
      let oDiv: HTMLElement | null = null;
      
      // 配列内の位置としては0から始まるが、questionlistのインデックスは1から始まる可能性がある
      // そのため、質問のインデックスを調整して正しい質問を取得する
      const questionIndex = i;
      
      if (!this.question[questionIndex]) {
        continue;
      }
      let currentQ = this.question[i].questionHtml?.replace(/&nbsp;/g, '') || '';
      let add = 0;
      
      if (next > i) {
        continue;
      }
      
      if (this.backup[i]) {
        // Reuse backed up element
        oDiv = this.backup[i];
        
        // Check for combined text fields
        let nextQ = this.question.length > (i+1) ? this.question[i+1].questionHtml?.replace(/&nbsp;/g, '') : null;
        while (currentQ.includes("aptoenq_textmargin") && nextQ != null && !nextQ.includes("questionTitle") && nextQ.includes("aptoenq_textmargin")) {
          add++;
          nextQ = this.question.length > (i+1+add) ? this.question[i+1+add].questionHtml?.replace(/&nbsp;/g, '') : null;
        }
      } else {
        // Skip attachments
        if (this.question[i].questionType === '5') {
          continue;
        }
        
        // Handle combined text fields
        let nextQ = this.question.length > (i+1) ? this.question[i+1].questionHtml?.replace(/&nbsp;/g, '') : null;
        while (currentQ.includes("aptoenq_textmargin") && nextQ != null && !nextQ.includes("questionTitle") && nextQ.includes("aptoenq_textmargin")) {
          currentQ += nextQ;
          add++;
          nextQ = this.question.length > (i+1+add) ? this.question[i+1+add].questionHtml?.replace(/&nbsp;/g, '') : null;
        }
        
        // Create new element from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = currentQ;
        oDiv = tempDiv.firstElementChild as HTMLElement;
        
        if (oDiv) {
          // Set attributes for the question
          oDiv.setAttribute('quesNo', i.toString());
          oDiv.setAttribute('data-type', this.question[i].questionType?.toString() || '');
          // 1. APIのrequired値を確認
          const requiredValue = this.question[i]?.required;
          let isRequired = requiredValue === true || requiredValue === 'true' || requiredValue === '1';
          
          // 2. HTML内に赤い*マークがあるかも確認（最も信頼できるマーカー）
          if (this.question[i]?.questionHtml) {
            const hasRedAsterisk = this.question[i]?.questionHtml?.includes('<span class="aptoenq_red">*</span>') || false;
            if (hasRedAsterisk) {
              isRequired = true;
            }
          }
          
          // 最終的な必須状態を設定
          oDiv.setAttribute('data-required', String(isRequired));
          oDiv.setAttribute('data-contentreflectflag', String(this.question[i].contentreflectflag || false));
          oDiv.setAttribute('data-answerafterline', String(this.question[i].answerafterline || '0'));
          oDiv.setAttribute('data-inputaidsubstitutiontype', String(this.question[i].inputaidsubstitutiontype || '0'));
          
          // Handle special form setup for radio/checkbox
          const questionType = this.question[i].questionType?.toString() || '';
          if (['1', '2'].includes(questionType)) {
            // Disable all text inputs initially
            const textInputs = oDiv.querySelectorAll('input[type="text"]');
            textInputs.forEach(input => {
              (input as HTMLInputElement).disabled = true;
            });
            
            // Enable text inputs for selected options
            // questionTypeは既に上で定義済み
            const inputs = oDiv.querySelectorAll('input[type="radio"],input[type="checkbox"]');
            
            inputs.forEach((element, index) => {
              const input = element as HTMLInputElement;
              if (input.checked) {
                // Build name for associated text input
                let attachedTextName = null;
                const elementNameIndex = index + 1;
                
                if (questionType === '1') {
                  // Radio button
                  attachedTextName = input.name;
                } else {
                  // Checkbox
                  attachedTextName = input.name.replace("[]", "");
                }
                attachedTextName += "sel" + elementNameIndex;
                
                // Enable associated text input
                if (oDiv) {
                  const textInputs = oDiv.querySelectorAll(`input[name="${attachedTextName}"]`);
                  textInputs.forEach(textInput => {
                    (textInput as HTMLInputElement).disabled = false;
                  });
                }
              }
            });
          }
        }
      }
      
      if (oDiv) {
        // Add to form
        this.aptoenq.appendChild(oDiv);
        
        // Add event handlers for input elements
        const inputs = oDiv.querySelectorAll('input[type="checkbox"], input[type="radio"]');
        for (let j = 0; j < inputs.length; j++) {
          const input = inputs[j] as HTMLInputElement;
          input.onclick = (() => {
            const currentI = i;
            return () => this.changed(input, currentI);
          })();
        }
        
        // Add event handlers for select elements
        const selects = oDiv.querySelectorAll('select');
        for (let j = 0; j < selects.length; j++) {
          const select = selects[j] as HTMLSelectElement;
          select.onchange = (() => {
            const currentI = i;
            return () => this.selchanged(select, currentI);
          })();
        }
        
        // Check for branching
        next = (this.question[i].next !== undefined && this.question[i].next !== "") 
          ? parseInt(String(this.question[i].next || 0), 10) - 1 
          : -1;
        this.question[i].enabled = true;
        
        if (this.question[i].branchMap) {
          let val = '';
          const inputs = oDiv.querySelectorAll('input');
          let setNext = false;
          
          // Check for selected inputs
          for (let j = 0; j < inputs.length; j++) {
            const input = inputs[j] as HTMLInputElement;
            if (input.checked) {
              val = input.value;
              if (val !== undefined && val !== '') {
                const branchMap = this.question[i].branchMap;
                
                // branchMapが存在することを型ガードで保証
                if (branchMap) {
                  // 安全なプロパティアクセス
                  if (Object.prototype.hasOwnProperty.call(branchMap, val)) {
                    const branchValue = branchMap[val];
                    // 値が存在し、nullでないことを確認
                    if (branchValue !== undefined && branchValue !== null && branchValue !== "") {
                      next = parseInt(String(branchValue), 10) - 1;
                      setNext = true;
                      break;
                    } else {
                      // 選択された値のbranch先がnullなら次の質問を表示せずに終了
                      setNext = true;
                      break;
                    }
                  }
                }
              }
            }
          }
          
          // Check for selected select options
          const selects = oDiv.querySelectorAll('select');
          if (selects.length === 1) {
            const select = selects[0] as HTMLSelectElement;
            if (select.selectedIndex > 0) {
              val = select.options[select.selectedIndex].value;
              if (val !== undefined && val !== '') {
                const branchMap = this.question[i].branchMap;
                
                // branchMapが存在することを型ガードで保証
                if (branchMap) {
                  // 安全なプロパティアクセス
                  if (Object.prototype.hasOwnProperty.call(branchMap, val)) {
                    const branchValue = branchMap[val];
                    // 値が存在し、nullでないことを確認
                    if (branchValue !== undefined && branchValue !== null && branchValue !== "") {
                      next = parseInt(String(branchValue), 10) - 1;
                      setNext = true;
                    } else {
                      // 選択された値のbranch先がnullなら次の質問を表示せずに終了
                      setNext = true;
                    }
                  }
                }
              }
            }
          }
          
          // Check if all branch map values are null
          let isNotAllNull = false;
          const branchMap = this.question[i].branchMap;
          
          // branchMapが存在することを型ガードで保証
          if (branchMap) {
            for (const key in branchMap) {
              // hasOwnPropertyを使用して安全にプロパティにアクセス
              if (Object.prototype.hasOwnProperty.call(branchMap, key)) {
                const value = branchMap[key];
                if (value !== null && value !== undefined) {
                  isNotAllNull = true;
                  break;
                }
              }
            }
          }
          
          // If we have branching but nothing selected, stop processing
          if (isNotAllNull && !setNext) {
            break;
          }
        }
        
        // Skip questions that were combined
        i = i + add;
      }
    }
    
    // Apply CSS
    if (this.css && this.aptoenq) {
      this.css.forEach(css => {
        if (css && css.length >= 2) {
          const elements = this.aptoenq?.querySelectorAll(css[0]);
          if (elements && elements.length > 0) {
            elements.forEach(el => {
              (el as HTMLElement).setAttribute('style', css[1]);
            });
          }
        }
      });
    }
    
    // Hide dummy elements
    if (this.aptoenq) {
      const dummies = this.aptoenq.querySelectorAll('.aptoenq_dummy');
      dummies.forEach(dummy => {
        (dummy as HTMLElement).style.display = 'none';
      });
      
      // Add wrapper div for selects - セレクタの修正
      const selects = this.aptoenq.querySelectorAll('.aptoenq_qa select:not(.addedDiv)');
      selects.forEach(select => {
        const wrapper = document.createElement('div');
        wrapper.className = 'selectBox';
        select.parentNode?.insertBefore(wrapper, select);
        wrapper.appendChild(select);
        select.classList.add('addedDiv');
      });
    }
  }
  
  /**
   * 単一の質問を表示する - 初期状態用
   */
  private displaySingleQuestion(questionIndex: number) {
    
    // 質問リストのチェック
    if (!this.aptoenq) {
      return;
    }
    
    if (!this.question || !Array.isArray(this.question)) {
      return;
    }
    
    if (this.question.length === 0) {
      return;
    }
    
    if (!this.question[questionIndex]) {
      // 配列にデータがあり、指定されたインデックスがない場合は最初の要素を使用
      if (this.question.length > 0) {
        questionIndex = 0;
      } else {
        return;
      }
    }
    
    
    // questionHtmlの存在チェック
    if (!this.question[questionIndex].questionHtml) {
      // questionHtmlがない場合、ダミーコンテンツを作成
      const dummyDiv = document.createElement('div');
      dummyDiv.className = 'aptoenq_qa';
      dummyDiv.innerHTML = `
        <div class="questionTitle">
          <div class="whitespace">質問${questionIndex + 1}</div>
        </div>
        <div class="aptoenq_a">
          <select>
            <option value="">選択してください</option>
            <option value="A">オプションA</option>
            <option value="B">オプションB</option>
          </select>
        </div>
      `;
      
      // イベントハンドラ設定
      this.setupEventHandlers(dummyDiv, questionIndex);
      
      // 属性を設定
      dummyDiv.setAttribute('quesNo', questionIndex.toString());
      dummyDiv.setAttribute('data-type', '3'); // select type
      dummyDiv.setAttribute('data-required', 'true');
      
      // コンテナに追加
      this.aptoenq.appendChild(dummyDiv);
      if (this.question[questionIndex]) {
        this.question[questionIndex].enabled = true;
      }
      
      return;
    }
    
    // Create question element
    let currentQ = this.question[questionIndex].questionHtml?.replace(/&nbsp;/g, '') || '';
    
    // Create new element from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentQ;
    const oDiv = tempDiv.firstElementChild as HTMLElement || tempDiv;
    
    if (oDiv) {
      // DIV要素であることを確認
      if (oDiv.tagName !== 'DIV') {
        // First element is not a DIV, wrapping content
        const wrapper = document.createElement('div');
        wrapper.className = 'aptoenq_qa';
        wrapper.appendChild(oDiv.cloneNode(true));

        // 回答欄なしタイプ（見出しのみ）を検出してクラスを追加
        if (this.isHeadingOnlyQuestion(questionIndex)) {
          wrapper.classList.add('heading-only');
          console.log(`質問${questionIndex}のwrapperに heading-only クラスを追加しました`);

          // 直接スタイルを適用して確実に非表示にする
          const answerDiv = wrapper.querySelector('.aptoenq_a');
          if (answerDiv) {
            (answerDiv as HTMLElement).style.display = 'none';
            (answerDiv as HTMLElement).style.visibility = 'hidden';
            (answerDiv as HTMLElement).style.height = '0';
            console.log(`質問${questionIndex}の回答欄を直接非表示にしました（displaySingleQuestion）`);
          }

          // 質問タイトルのスタイルを調整
          const titleDiv = wrapper.querySelector('.questionTitle');
          if (titleDiv) {
            (titleDiv as HTMLElement).style.borderBottom = 'none';
            (titleDiv as HTMLElement).style.borderRadius = '6px';
          }
        }

        this.aptoenq.appendChild(wrapper);
      } else {
        // クラス名を確保
        if (!oDiv.className.includes('aptoenq_qa')) {
          oDiv.className = oDiv.className ? oDiv.className + ' aptoenq_qa' : 'aptoenq_qa';
        }
        
        // Set attributes for the question
        oDiv.setAttribute('quesNo', questionIndex.toString());
        oDiv.setAttribute('data-type', this.question[questionIndex].questionType?.toString() || '');
        // 1. APIのrequired値を確認
        const requiredValue = this.question[questionIndex].required;
        let isRequired = requiredValue === true || requiredValue === 'true' || requiredValue === '1';
        
        // 2. HTML内に赤い*マークがあるかも確認（最も信頼できるマーカー）
        if (this.question[questionIndex]?.questionHtml) {
          const hasRedAsterisk = this.question[questionIndex]?.questionHtml?.includes('<span class="aptoenq_red">*</span>') || false;
          if (hasRedAsterisk) {
            isRequired = true;
          }
        }
        
        // 最終的な必須状態を設定
        oDiv.setAttribute('data-required', String(isRequired));
        oDiv.setAttribute('data-contentreflectflag', String(this.question[questionIndex].contentreflectflag || false));
        oDiv.setAttribute('data-answerafterline', String(this.question[questionIndex].answerafterline || '0'));
        oDiv.setAttribute('data-inputaidsubstitutiontype', String(this.question[questionIndex].inputaidsubstitutiontype || '0'));

        // 回答欄なしタイプ（見出しのみ）を検出してクラスを追加
        if (this.isHeadingOnlyQuestion(questionIndex)) {
          oDiv.classList.add('heading-only');
          console.log(`質問${questionIndex}に heading-only クラスを追加しました`);

          // 直接スタイルを適用して確実に非表示にする
          const answerDiv = oDiv.querySelector('.aptoenq_a');
          if (answerDiv) {
            (answerDiv as HTMLElement).style.display = 'none';
            (answerDiv as HTMLElement).style.visibility = 'hidden';
            (answerDiv as HTMLElement).style.height = '0';
            console.log(`質問${questionIndex}の回答欄を直接非表示にしました（displaySingleQuestion）`);
          }

          // 質問タイトルのスタイルを調整
          const titleDiv = oDiv.querySelector('.questionTitle');
          if (titleDiv) {
            (titleDiv as HTMLElement).style.borderBottom = 'none';
            (titleDiv as HTMLElement).style.borderRadius = '6px';
          }
        }

        this.restructureLabelsWithAddText(oDiv);

        // イベントハンドラの設定を改善したヘルパーメソッドを使用
        this.setupEventHandlers(oDiv, questionIndex);

        // Append to container
        this.aptoenq.appendChild(oDiv);
        this.question[questionIndex].enabled = true;
      }
      
      // Apply CSS
      if (this.css && this.aptoenq) {
        this.css.forEach(css => {
          if (css && css.length >= 2) {
            const elements = this.aptoenq?.querySelectorAll(css[0]);
            if (elements && elements.length > 0) {
              elements.forEach(el => {
                (el as HTMLElement).setAttribute('style', css[1]);
              });
            }
          }
        });
      }
      
      // Add wrapper div for selects
      const selectElements = this.aptoenq.querySelectorAll('select:not(.addedDiv)');
      selectElements.forEach(select => {
        const wrapper = document.createElement('div');
        wrapper.className = 'selectBox';
        select.parentNode?.insertBefore(wrapper, select);
        wrapper.appendChild(select);
        select.classList.add('addedDiv');
      });
      
    } else {
      // Failed to create element from HTML
    }
  }

  /**
   * イベントハンドラを設定するヘルパーメソッド
   */
  private setupEventHandlers(element: HTMLElement, questionIndex: number) {
    // ラジオボタンのイベントハンドラ設定
    const radios = element.querySelectorAll('input[type="radio"]');
    
    for (let j = 0; j < radios.length; j++) {
      const radio = radios[j] as HTMLInputElement;
      // クリックイベントとchangeイベントの両方に対応
      radio.onclick = () => {
        this.changed(radio, questionIndex);
      };
      radio.onchange = () => {
        this.changed(radio, questionIndex);
      };
      
      // ラベルクリックのサポート
      const parentLabel = radio.closest('label');
      if (parentLabel) {
        parentLabel.onclick = (e) => {
          // 直接ラジオボタンをクリック
          if (!radio.checked) {
            radio.checked = true;
            this.changed(radio, questionIndex);
          }
        };
      }
    }
    
    // チェックボックスのイベントハンドラ設定
    const checkboxes = element.querySelectorAll('input[type="checkbox"]');
    
    for (let j = 0; j < checkboxes.length; j++) {
      const checkbox = checkboxes[j] as HTMLInputElement;
      // クリックイベントとchangeイベントの両方に対応
      checkbox.onclick = () => {
        this.handleCheckboxChange(checkbox, questionIndex);
      };
      checkbox.onchange = () => {
        this.handleCheckboxChange(checkbox, questionIndex);
      };
      
      // 初期表示時にラベルクリックのイベントはつけない
      // (チェックボックス自体のクリックイベントだけで十分)
      
      // 初期状態のチェックに基づいてテキスト入力の状態を設定
      if (checkbox.checked) {
        this.enableAssociatedTextInput(checkbox);
      }
    }
    
    // セレクトボックスのイベントハンドラ設定
    const selects = element.querySelectorAll('select');
    
    for (let j = 0; j < selects.length; j++) {
      const select = selects[j] as HTMLSelectElement;
      select.onchange = () => {
        this.selchanged(select, questionIndex);
      };
    }
  }
  
  /**
   * チェックボックスの変更を処理する
   */
  private handleCheckboxChange(checkbox: HTMLInputElement, questionIndex: number) {
    // テキスト入力フィールドの有効/無効を切り替え
    if (checkbox.checked) {
      this.enableAssociatedTextInput(checkbox);
    } else {
      this.disableAssociatedTextInput(checkbox);
    }
    
    // 分岐処理などの共通処理
    this.changed(checkbox, questionIndex);
  }
  
  /**
   * チェックボックスに関連するテキスト入力を有効化
   */
  private enableAssociatedTextInput(checkbox: HTMLInputElement) {
    const label = checkbox.closest('label');
    if (!label) return;
    
    // 同じラベル内のテキスト入力を有効化
    const textInputs = label.querySelectorAll('input[type="text"]');
    textInputs.forEach(input => {
      (input as HTMLInputElement).disabled = false;
    });
  }
  
  /**
   * チェックボックスに関連するテキスト入力を無効化
   */
  private disableAssociatedTextInput(checkbox: HTMLInputElement) {
    const label = checkbox.closest('label');
    if (!label) return;
    
    // 同じラベル内のテキスト入力を無効化
    const textInputs = label.querySelectorAll('input[type="text"]');
    textInputs.forEach(input => {
      (input as HTMLInputElement).disabled = true;
    });
  }

  /**
   * Remove questions after a certain position
   */
  deleteAfter(no: number) {
    if (!this.aptoenq) return;
    
    for (let i = this.aptoenq.childNodes.length - 1; i >= 0; i--) {
      const c = this.aptoenq.childNodes[i] as HTMLElement;
      
      if (!c.className || c.className !== "aptoenq_qa") {
        continue;
      }
      
      const quesNo = parseInt(c.getAttribute('quesNo') || '-1');
      if (quesNo <= no) {
        continue;
      }
      
      if (this.question[quesNo]) {
        this.question[quesNo].enabled = false;
      }
      
      this.backup[quesNo] = c;
      this.aptoenq.removeChild(c);
    }
  }

  /**
   * フォーム要素が属する設問のインデックスを取得する
   */
  private getQuestionIndexFromElement(element: Element): number {
    const wrapper = element.closest('.aptoenq_qa') as HTMLElement | null;
    return parseInt(wrapper?.getAttribute('quesNo') || '0', 10);
  }

  /**
   * リセット時に設定すべきテキスト初期値を返す。
   * 自由テキスト設問（questionType=4）で初期値があれば復元、なければ空文字。
   */
  private getResetTextValue(questionIndex: number): string {
    const q = this.question[questionIndex];
    if (q?.questionType?.toString() === '4' && q.defaultanswertext) {
      return q.defaultanswertext;
    }
    return '';
  }

  /**
   * Reset the form
   */
  reset() {
    if (!this.question || !this.aptoenq) {
      return;
    }
    
    // Find all questions
    const allQuestions = this.aptoenq.querySelectorAll('.aptoenq_qa');
    if (!allQuestions || allQuestions.length === 0) {
      // No questions found, recreating form
      
      // 新しいアプローチ：全ての質問を再生成
      this.aptoenq.innerHTML = '';
      
      // 初期表示する質問のインデックスを取得
      const initialQuestionIndices = this.getInitialDisplayQuestions();
      
      // 全ての質問を作成して追加
      for (let i = 0; i < this.question.length; i++) {
        // 質問要素を作成
        const questionElement = this.createQuestionElement(i);
        if (questionElement) {
          // 初期表示対象以外は非表示に
          if (!initialQuestionIndices.includes(i)) {
            questionElement.style.display = 'none';
          }
          
          // フォームに追加
          this.aptoenq.appendChild(questionElement);
          
          // イベントハンドラを設定
          this.setupEventHandlers(questionElement, i);
          
          // 有効としてマーク
          this.question[i].enabled = true;
        }
      }
      
      // CSSを適用
      this.applyCss();

      // 初期選択肢がある場合の分岐処理
      this.processInitialSelections();
    } else {
      // Questions found, resetting display states
      
      // 全ての入力フィールドをリセット
      const inputs = this.aptoenq.querySelectorAll('input');
      inputs.forEach(input => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          // まず全てのチェックを外す
          (input as HTMLInputElement).checked = false;
          
          // 元のHTMLでchecked属性があるかチェック
          const questionIndex = this.getQuestionIndexFromElement(input);

          if (this.question[questionIndex] && this.question[questionIndex].questionHtml) {
            const inputId = input.id;
            const inputValue = (input as HTMLInputElement).value;
            const questionHtml = this.question[questionIndex].questionHtml;
            
            // TypeScriptのエラー回避のために明示的な型チェックとアサーション
            const safeQuestionHtml = questionHtml as string;
            
            // inputタグでcheckedがある要素を検索
            if (inputId && typeof safeQuestionHtml === 'string') {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = safeQuestionHtml;
              const originalInput = tempDiv.querySelector(`[id="${inputId}"]`);
              if (originalInput && originalInput.hasAttribute('checked')) {
                (input as HTMLInputElement).checked = true;
              }
            }
          }
        } else {
          // テキスト入力は初期値があれば復元、なければクリア
          const questionIndex = this.getQuestionIndexFromElement(input);
          (input as HTMLInputElement).value = this.getResetTextValue(questionIndex);
        }
      });
      
      // セレクトボックスのリセット処理 - selected属性を持つものは維持
      const selects = this.aptoenq.querySelectorAll('select');
      selects.forEach(select => {
        const selectElement = select as HTMLSelectElement;

        // 質問インデックスを取得
        const questionIndex = this.getQuestionIndexFromElement(selectElement);

        if (this.question[questionIndex] && this.question[questionIndex].questionHtml) {
          const questionHtml = this.question[questionIndex].questionHtml;
          
          // オプションをリセット
          let selectedOptionFound = false;

          // selected属性を持つoptionの値をDOM解析で正確に取得。
          if (questionHtml) {
            const initialSelectedValue = this.getInitialSelectedValueFromQuestion(this.question[questionIndex]);
            if (initialSelectedValue) {
              for (let i = 0; i < selectElement.options.length; i++) {
                if (selectElement.options[i].value === initialSelectedValue) {
                  selectElement.selectedIndex = i;
                  selectedOptionFound = true;
                  break;
                }
              }
            }
          }
          
          // selected属性を持つoptionがなければインデックス0に戻す
          if (!selectedOptionFound) {
            selectElement.selectedIndex = 0;
          }
        } else {
          selectElement.selectedIndex = 0;
        }
      });
      
      // テキストエリアもリセット
      const textareas = this.aptoenq.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        // 初期設定のある設問は、空クリアではなく初期値に復元。
        const questionIndex = this.getQuestionIndexFromElement(textarea);
        (textarea as HTMLTextAreaElement).value = this.getResetTextValue(questionIndex);
      });
      
      // 初期表示する質問のインデックスを取得
      const initialQuestionIndices = this.getInitialDisplayQuestions();
      
      // 質問の表示状態をリセット - 初期表示対象のみ表示
      allQuestions.forEach((question, index) => {
        const shouldDisplay = initialQuestionIndices.includes(index);
        (question as HTMLElement).style.display = shouldDisplay ? '' : 'none';
      });
      
      // 初期選択肢がある場合の分岐処理
      this.processInitialSelections();
    }
    
    // 状態をリセット
    this.no = 0;
    this.backup = {};
    
  }
  
}

/**
 * SelectEnqueteInputAidModal class to manage assist input functionality
 */
export class SelectEnqueteInputAidModal {
  static selectNo: number | null = null;
  static callback: ((value: string) => void) | null = null;
  static enquete: any = null;
  static kind: string | null = null;
  static excludeFunc: string | null = null;
  static selectedEnqName: string | null = null; // 選択されたアシストオプションのタイトル

  /**
   * Open the modal
   */
  static showModal(callback: (value: string) => void, kind: string = 'customerRemarks', excludeFunc: string | null = null) {
    SelectEnqueteInputAidModal.clear();

    // Set up callback and parameters
    SelectEnqueteInputAidModal.callback = callback;
    SelectEnqueteInputAidModal.kind = kind;
    SelectEnqueteInputAidModal.excludeFunc = excludeFunc;

    // Trigger dropdown change if there's a pre-selected value
    const selectedVal = document.getElementById(`selectEnqueteInputAid_${kind}`)?.getAttribute('value') || '';
    if (selectedVal) {
      const dropdown = document.getElementById('selectEnqueteInputAid_menu') as HTMLSelectElement;
      if (dropdown) {
        dropdown.value = selectedVal;
        dropdown.dispatchEvent(new Event('change'));
      }
    }
  }

  /**
   * Check input validation
   */
  static checkInput(): boolean {
    let errorMessage = "";
    const aptoenqform = document.getElementById('aptoenqform');
    
    if (!aptoenqform) return true;
    
    // Find all question elements with aptoenq_qa class
    const questions = aptoenqform.querySelectorAll('.aptoenq_qa');
    // フィルタリングして子divを持つ要素のみを処理し、非表示の設問は除外する
    Array.from(questions)
      .filter(q => q.querySelector('div') && getComputedStyle(q).display !== 'none')
      .forEach(question => {
        const q = question as HTMLElement;
        let isError = false;
        
        // Get data attributes
        const type = q.getAttribute('data-type');
        const required = q.getAttribute('data-required')?.toLowerCase() === 'true';
        
        if (required) {
          if (['1', '2'].includes(type || '')) {
            // Radio or checkbox
            const checked = q.querySelectorAll('input[type="radio"]:checked,input[type="checkbox"]:checked');
            if (checked.length === 0) {
              isError = true;
            }
          } else if (type === '3') {
            // Select
            const select = q.querySelector('select') as HTMLSelectElement;
            if (select && select.value === "") {
              isError = true;
            }
          } else if (type === '4') {
            // Text input
            const textInputs = q.querySelectorAll('input[type="text"],textarea');
            if (textInputs.length === 1) {
              // Single text field
              if ((textInputs[0] as HTMLInputElement).value.length === 0) {
                isError = true;
              }
            } else {
              // Multiple text fields with required markers
              const requiredFields = q.querySelectorAll('.aptoenq_a .aptoenq_red');
              requiredFields.forEach(redMarker => {
                const nextInput = FindNextSiblingBySelector(redMarker as HTMLElement, 'input[type="text"],textarea');
                if (nextInput && (nextInput as HTMLInputElement).value.length === 0) {
                  isError = true;
                }
              });
            }
          }
        }
        
        if (isError) {
          // Get question text
          const questionText = SelectEnqueteInputAidModal.getQuestionString(q, true);
          errorMessage += questionText + "<br>";
        }
      });
    
    if (errorMessage.length > 0) {
      // Show error to user
      const errorText = '必須項目が入力されていません:\n\n' + errorMessage.replace(/<br>/g, '\n');
      console.log('エラー発生:', errorText);
      
      // アシスト入力モーダル内にエラーメッセージを表示する方法に変更
      if (typeof window !== 'undefined' && (window as any).showAssistErrorInModal) {
        console.log('モーダル内エラー表示を呼び出し');
        (window as any).showAssistErrorInModal(errorText);
      } else {
        console.log('フォールバック: alertを使用');
        // Fallback to alert if modal function is not available
        alert(errorText);
      }
      return false;
    }
    
    return true;
  }

  /**
   * Get question string from an element
   */
  static getQuestionString(enquete: HTMLElement, required?: boolean): string {
    required = required ? true : false;

    const type = enquete.getAttribute('data-type');

    // セレクトボックス（type=3）、回答欄なしタイプ（type=6）、または明示的に必須チェック（required=true）の場合は特別処理
    if (type === '3' || type === '6' || required === true) {
      // 質問タイトル要素を取得（questionTitleとquestionTitleNoAnsの両方を試す）
      let questionTitle = enquete.querySelector('.questionTitle');
      if (!questionTitle) {
        questionTitle = enquete.querySelector('.questionTitleNoAns');
      }
      if (questionTitle) {
        // まずwhitespaceクラスからテキストを取得してみる
        const whitespace = questionTitle.querySelector('.whitespace');
        if (whitespace && whitespace.innerHTML) {
          return whitespace.innerHTML.replace(/<br\s*\/?>/gi, '\n').replace(/^[ \t]+|[ \t]+$/g, '');
        }

        // セレクトボックス、回答欄なしタイプ、または必須チェックの場合はテキスト全体を取得
        if (questionTitle.textContent) {
          // 必須マーク(*)を除去し、不要な空白を整理してテキストを返す
          return questionTitle.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
        }
      }
    }

    // contentReflectFlagが'true'かつrequiredがfalseの場合は空文字を返す
    // ただし必須チェック時(required=true)は常に文字列を返す
    const contentReflectFlag = enquete.getAttribute('data-contentreflectflag') === 'true';
    if (contentReflectFlag && !required) {
      return '';
    }

    // 質問タイトル要素を取得（questionTitleとquestionTitleNoAnsの両方を試す）
    let questionTitle = enquete.querySelector('.questionTitle');
    if (!questionTitle) {
      questionTitle = enquete.querySelector('.questionTitleNoAns');
    }
    if (questionTitle) {
      // まずwhitespaceクラスを持つ要素から取得を試みる
      const whitespace = questionTitle.querySelector('.whitespace');
      if (whitespace && whitespace.innerHTML) {
        return whitespace.innerHTML.replace(/<br\s*\/?>/gi, '\n').replace(/^[ \t]+|[ \t]+$/g, '');
      }

      // whitespaceが取得できなかった場合は、questionTitle全体のテキストを返す
      if (questionTitle.textContent) {
        // 必須マーク(*)を除去してテキストを返す
        return questionTitle.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
      }
    }

    return '';
  }

  /**
   * 表示されている必須項目の入力チェック
   * 未入力の必須項目がある場合はアラートを表示してfalseを返す
   */
  static validateVisibleRequiredQuestions(): boolean {
    let errorMessage = "";
    const aptoenqform = document.getElementById('aptoenqform');
    
    if (!aptoenqform) return true;
    
    // 表示されている質問要素のみを対象にする
    const questions = aptoenqform.querySelectorAll('.aptoenq_qa');
    
    
    Array.from(questions).forEach(question => {
      const q = question as HTMLElement;
      
      // 非表示の質問はスキップ（計算済みスタイルで判定）
      if (getComputedStyle(q).display === 'none') {
        return;
      }
      
      let isError = false;
      
      // 必須マーカー(*)がHTML内に存在するかチェック - HTML内の*マークを最優先
      const hasRequiredMarker = q.querySelector('.aptoenq_red') !== null;
      
      // data-required属性の値を取得 - さまざまな形式に対応
      const requiredAttr = q.getAttribute('data-required');
      const isRequiredByAttr = requiredAttr === 'true' || requiredAttr === '1' || requiredAttr === 'yes';
      
      // HTML内の*マークまたはdata-required属性がtrueの場合に必須とみなす
      const required = hasRequiredMarker || isRequiredByAttr;
      
      
      if (!required) {
        return;
      }
      
      // 質問タイプに応じたバリデーション
      const type = q.getAttribute('data-type');
      
      if (['1', '2'].includes(type || '')) {
        // ラジオボタンまたはチェックボックス
        const checked = q.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checked.length === 0) {
          isError = true;
        }
      } else if (type === '3') {
        // セレクトボックス
        const select = q.querySelector('select') as HTMLSelectElement;
        if (select && select.value === "") {
          isError = true;
        }
      } else if (type === '4') {
        // テキスト入力
        const textInputs = q.querySelectorAll('input[type="text"], textarea');
        if (textInputs.length === 1) {
          // 単一のテキストフィールド
          if ((textInputs[0] as HTMLInputElement).value.trim().length === 0) {
            isError = true;
          }
        } else {
          // 複数のテキストフィールドで必須マーカーがあるもの
          const requiredFields = q.querySelectorAll('.aptoenq_a .aptoenq_red');
          requiredFields.forEach(redMarker => {
            const nextInput = FindNextSiblingBySelector(redMarker as HTMLElement, 'input[type="text"], textarea');
            if (nextInput && (nextInput as HTMLInputElement).value.trim().length === 0) {
              isError = true;
            }
          });
        }
      }
      
      // エラーがあれば質問テキストを取得してエラーメッセージに追加
      if (isError) {
        const questionText = SelectEnqueteInputAidModal.getQuestionString(q, true);
        errorMessage += questionText + "\n";
      }
    });
    
    // エラーメッセージがあればアラートを表示してfalseを返す
    if (errorMessage.length > 0) {
      const errorText = '必須項目が入力されていません:\n\n' + errorMessage;
      console.log('エラー発生 (2箇所目):', errorText);
      
      // アシスト入力モーダル内にエラーメッセージを表示する方法に変更
      if (typeof window !== 'undefined' && (window as any).showAssistErrorInModal) {
        console.log('モーダル内エラー表示を呼び出し (2箇所目)');
        (window as any).showAssistErrorInModal(errorText);
      } else {
        console.log('フォールバック: alertを使用 (2箇所目)');
        // Fallback to alert if modal function is not available
        alert(errorText);
      }
      return false;
    }
    
    return true;
  }

  /**
   * Confirm the selected enquete
   */
  static confirmEnquete() {
    if (!SelectEnqueteInputAidModal.enquete) {
      return;
    }

    // 表示されている必須項目の入力チェック
    if (!SelectEnqueteInputAidModal.validateVisibleRequiredQuestions()) {
      return; // バリデーションエラーがある場合は処理を中断
    }
    
    let message = '';

    // Add enquete name if needed
    if (SelectEnqueteInputAidModal.enquete && SelectEnqueteInputAidModal.enquete.enqnameresultflag === true) {
      if (SelectEnqueteInputAidModal.selectedEnqName) {
        message = SelectEnqueteInputAidModal.selectedEnqName + '\n';
      }
    }

    // Get form responses
    const aptoenqform = document.getElementById('aptoenqform');
    if (aptoenqform) {
      const questions = aptoenqform.querySelectorAll('.aptoenq_qa');

      // 表示されている設問のみを配列に取得
      const visibleQuestions = Array.from(questions).filter(
        q => getComputedStyle(q).display !== 'none'
      );

      // 実際にmessageに追加した設問数を追跡
      let addedQuestionCount = 0;

      visibleQuestions.forEach((enquete, index) => {
        // 回答欄なしタイプ（questionType=6）かどうかをチェック
        const questionType = (enquete as HTMLElement).getAttribute('data-type');
        const isHeadingOnly = questionType === '6';

        const answer = enquete.querySelector('.aptoenq_a');

        // 回答欄なしタイプでない場合は、answer要素とinput要素が必須
        if (!isHeadingOnly) {
          if (!answer) return;

          // Get answer type
          const firstInput = answer.querySelector('input,select,textarea') as HTMLElement;
          if (!firstInput) return;
        }

        let answerType = '';
        if (answer) {
          const firstInput = answer.querySelector('input,select,textarea') as HTMLElement;
          if (firstInput) {
            answerType = firstInput.tagName.toLowerCase();
            if (answerType === 'input') {
              answerType = (firstInput as HTMLInputElement).type.toLowerCase();
            }
          }
        }

        // Get answer content
        let answerText = '';

        // 回答欄なしタイプの場合、answerがnullなので回答取得処理をスキップ
        if (answer) {
          if (answerType === 'text' || answerType === 'textarea') {
            // テキスト入力またはテキストエリアの場合

          // 前付きテキストがあれば取得
          const beforeSpan = answer.querySelector(`[name^="before_ans"]`);
          if (beforeSpan && beforeSpan instanceof HTMLElement) {
            answerText += beforeSpan.innerText + " ";
          }
          
          // 入力欄の値を取得
          const inputElement = answer.querySelector('input[type="text"], textarea') as HTMLInputElement | HTMLTextAreaElement;
          if (inputElement) {
            answerText += inputElement.value;
          }
          
          // 後付きテキストがあれば取得
          const afterSpan = answer.querySelector(`[name^="after_ans"]`);
          if (afterSpan && afterSpan instanceof HTMLElement) {
            answerText += " " + afterSpan.innerText;
          }
        } else if (answerType === 'radio') {
          // Radio button
          const selected = answer.querySelector('input[type="radio"]:checked') as HTMLInputElement;
          if (selected) {
            // Input value
            answerText += selected.value;

            // Associated text input if any
            const label = selected.closest('label');
            if (label) {
              // before_ans要素があれば取得して表示
              const beforeSpan = label.querySelector(`[name^="before_ans"]`);
              if (beforeSpan && beforeSpan instanceof HTMLElement) {
                answerText += " " + beforeSpan.innerText;
              }

              // テキスト入力があれば取得して表示（aptoenq_addtextクラスの場合はコロンを追加）
              const textInput = label.querySelector('input[type="text"]') as HTMLInputElement;
              if (textInput && textInput.value) {
                if (textInput.classList.contains('aptoenq_addtext')) {
                  answerText += '：';
                }
                answerText += textInput.value;
              }

              // after_ans要素があれば取得して表示
              const afterSpan = label.querySelector(`[name^="after_ans"]`);
              if (afterSpan && afterSpan instanceof HTMLElement) {
                answerText += " " + afterSpan.innerText;
              }
            }
          }
        } else if (answerType === 'select') {
          // Select dropdown
          const select = answer.querySelector('select') as HTMLSelectElement;
          if (select && select.value !== '') {
            // Selected option value
            answerText += select.value;
            
            // Associated text input for "Other" option
            const textInput = answer.querySelector('input[type="text"]:not([style*="display: none"])') as HTMLInputElement;
            if (textInput) {
              answerText += textInput.value;
            }
          }
        } else if (answerType === 'checkbox') {
          // Checkbox
          const checked = answer.querySelectorAll('input[type="checkbox"]:checked');
          checked.forEach((checkbox, idx) => {
            const cb = checkbox as HTMLInputElement;

            if (idx > 0) {
              const substitutionType = (enquete as HTMLElement).getAttribute('data-inputaidsubstitutiontype') || '0';
              if (substitutionType === '0') {
                answerText += '/';
              } else {
                answerText += '\n';
              }
            }

            // Input value
            answerText += cb.value;

            // Associated text input if any
            const label = cb.closest('label');
            if (label) {
              // before_ans要素があれば取得して表示
              const beforeSpan = label.querySelector(`[name^="before_ans"]`);
              if (beforeSpan && beforeSpan instanceof HTMLElement) {
                answerText += " " + beforeSpan.innerText;
              }

              // テキスト入力があれば取得して表示（aptoenq_addtextクラスの場合はコロンを追加）
              const textInput = label.querySelector('input[type="text"]') as HTMLInputElement;
              if (textInput && textInput.value) {
                if (textInput.classList.contains('aptoenq_addtext')) {
                  answerText += '：';
                }
                answerText += textInput.value;
              }

                // after_ans要素があれば取得して表示
                const afterSpan = label.querySelector(`[name^="after_ans"]`);
                if (afterSpan && afterSpan instanceof HTMLElement) {
                  answerText += " " + afterSpan.innerText;
                }
              }
            });
          }
        }
        
        // contentreflectflagを取得（falseの場合は設問文を挿入、trueの場合は挿入しない）
        const contentReflectFlag = (enquete as HTMLElement).getAttribute('data-contentreflectflag');
        const shouldIncludeQuestion = contentReflectFlag !== 'true';

        // 設問文を取得
        const questionText = SelectEnqueteInputAidModal.getQuestionString(enquete as HTMLElement);

        // 回答欄なしタイプで設問文もanswerTextもない場合はスキップ（空白行を追加しない）
        if (isHeadingOnly && !shouldIncludeQuestion && (!answerText || answerText.trim().length === 0)) {
          return; // 何も追加せずにスキップ
        }

        // 通常の設問でanswerTextもquestionTextもない場合もスキップ
        if (!answerText || answerText.trim().length === 0) {
          if (!shouldIncludeQuestion || !questionText || questionText.trim().length === 0) {
            return; // 何も追加せずにスキップ
          }
        }

        // Add separator between questions (実際に追加した設問数で判定)
        if (addedQuestionCount > 0) {
          message += '\n';
        }

        // contentreflectflagがfalseの場合のみ設問文を追加
        if (shouldIncludeQuestion && questionText && questionText.trim().length > 0) {
          message += questionText + answerText;
        } else {
          message += answerText;
        }

        // 追加した設問数をインクリメント
        addedQuestionCount++;

        // Add extra lines after answer if configured
        const answerAfterLine = parseInt((enquete as HTMLElement).getAttribute('data-answerafterline') || '0');
        if (answerAfterLine > 0) {
          for (let cnt = 0; cnt < answerAfterLine; cnt++) {
            message += '\n';
          }
        }
      });
    }
    
    // コールバック関数を実行して入力を適用
    if (SelectEnqueteInputAidModal.callback) {
      SelectEnqueteInputAidModal.callback(message);
    }
    
    // モーダルを閉じる - wrapClassNameを持つ最も近い親要素を取得
    const modalWrap = document.querySelector('.selectEnqueteInputAid');
    if (modalWrap) {
      // Ant DesignのModalは直接styleで非表示にするのではなく、
      // 内部のstate経由で閉じるため、モーダルを閉じるイベントを発生させる
      const closeButton = modalWrap.querySelector('.ant-modal-close');
      if (closeButton && closeButton instanceof HTMLElement) {
        closeButton.click();
      }
    }
  }

  /**
   * Clear the form
   */
  static clear() {
    const aptoenqform = document.getElementById('aptoenqform');
    if (aptoenqform) {
      aptoenqform.classList.add('d-none');
      aptoenqform.innerHTML = '';
    }
    
    const dropdown = document.getElementById('selectEnqueteInputAid_menu') as HTMLSelectElement;
    if (dropdown) {
      dropdown.value = '';
    }
    
    SelectEnqueteInputAidModal.selectNo = null;
    SelectEnqueteInputAidModal.enquete = null;
    
    const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
    if (resetBtn) {
      resetBtn.setAttribute('disabled', 'disabled');
    }
    
    const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
    if (selectBtn) {
      selectBtn.setAttribute('disabled', 'disabled');
    }
  }
}

/**
 * Helper function to find the next sibling matching a selector
 */
function FindNextSiblingBySelector(element: HTMLElement, selector: string): HTMLElement | null {
  let current = element.nextElementSibling as HTMLElement | null;
  
  while (current) {
    if (current.matches(selector)) {
      return current;
    }
    current = current.nextElementSibling as HTMLElement | null;
  }
  
  return null;
}